/**
 * ArnLoadMore — Storefront API-driven PLP with client-side card assembly.
 *
 * This file is organised in #region blocks, each one a logically isolated
 * concern. Collapse them in your editor to navigate the file quickly.
 *
 * Top-down reading order matches the runtime flow:
 *   1. Configuration is resolved from window.arnPlpConfig at load time.
 *   2. The Storefront client + URL parser provide raw product data.
 *   3. The skeleton manager + card renderer turn that data into DOM.
 *   4. The media block manager interleaves promotional content.
 *   5. The Discount Kit helper refreshes storefront prices after each batch.
 *   6. The Tealium tracker maintains the analytics data layer in sync with
 *      the visible grid.
 *   7. ArnLoadMore orchestrates everything via lifecycle hooks.
 */

// #region Configuration & Environment
// ════════════════════════════════════════════════════════════════════════════

/**
 * ─── Tuning constants ────────────────────────────────────────────────────────
 * These values are tuned for a real-world production use case:
 *
 *   - Storefront API calls are minimised: sibling handles are resolved in a
 *     single batched GraphQL query (one HTTP round-trip per batch regardless
 *     of how many handles are requested).
 *   - PRODUCTS_PER_FETCH is set to 48 to maximise the number of products
 *     resolved per Storefront query (just under the hard Shopify limit of 50).
 *     This reduces the chance of needing a second Storefront page fetch to
 *     satisfy TARGET_CARDS_PER_LOAD, at the cost of occasionally fetching
 *     products that are deduplicated away and never rendered.
 *   - The grid uses a 4-column desktop layout, so RENDER_CONCURRENCY is set
 *     to a full row (4) for the cleanest visual fill pattern.
 *   - TARGET_CARDS_PER_LOAD is set to 24 so the initial viewport is fully
 *     populated (6 rows × 4 columns) without an immediate "Load More" press.
 *     Change to 8 if you want "Load More" to reveal exactly 2 rows at a time.
 *
 * All values below are used as fallbacks when arnPlpConfig.engine is absent.
 * The canonical source of truth is the section settings in the Shopify theme
 * editor, surfaced via window.arnPlpConfig.engine (set in
 * arn-main-collection-product-grid.liquid).
 */
const ARN_CFG_DEFAULTS = {
  PRODUCTS_PER_FETCH: 48,
  TARGET_CARDS_PER_LOAD: 24,
  RENDER_CONCURRENCY: 4,
  SIBLING_BATCH_SIZE: 100,
  RENDER_CHUNK_DELAY_MS: 20,
};

/**
 * Resolve the active ARN_CFG from arnPlpConfig.engine (set by the section
 * settings in the theme editor) with ARN_CFG_DEFAULTS as fallback.
 * Called once at runtime so the constants are fixed for the page lifetime.
 */
function _resolveArnCfg() {
  const eng = window.arnPlpConfig?.engine || {};
  return {
    PRODUCTS_PER_FETCH: eng.productsPerFetch ?? ARN_CFG_DEFAULTS.PRODUCTS_PER_FETCH,
    TARGET_CARDS_PER_LOAD: eng.targetCardsPerLoad ?? ARN_CFG_DEFAULTS.TARGET_CARDS_PER_LOAD,
    RENDER_CONCURRENCY: eng.renderConcurrency ?? ARN_CFG_DEFAULTS.RENDER_CONCURRENCY,
    SIBLING_BATCH_SIZE: eng.siblingBatchSize ?? ARN_CFG_DEFAULTS.SIBLING_BATCH_SIZE,
    RENDER_CHUNK_DELAY_MS: eng.renderChunkDelayMs ?? ARN_CFG_DEFAULTS.RENDER_CHUNK_DELAY_MS,
  };
}

/**
 * Resolve the active market context from arnPlpConfig.market (set by Liquid
 * in arn-main-collection-product-grid.liquid).
 *
 * The country and language codes are passed to the Storefront API via the
 * @inContext directive so that each market gets its own cache key on Shopify's
 * edge layer — preventing cross-market cache pollution where, for example,
 * it-IT cached results are served to en-gb visitors.
 *
 * Falls back to null values when the config is absent (e.g. during local dev),
 * in which case @inContext defaults are used.
 */
function _resolveMarketContext() {
  const mkt = window.arnPlpConfig?.market || {};
  return {
    country: mkt.country || null,
    language: mkt.language || null,
    handle: mkt.handle || null,
  };
}

// Resolved once at module evaluation time.
const ARN_CFG = _resolveArnCfg();
const ARN_MARKET = _resolveMarketContext();

// #endregion

// #region Debug Flags & Loggers
// ════════════════════════════════════════════════════════════════════════════

/**
 * Debug flags driven by arnPlpConfig.engine (section settings).
 * Fallback to false when the config key is absent so production is always
 * clean without requiring an explicit opt-out.
 *
 * ARN_DEBUG_BADGES — when true, JS-injected sibling slides receive a small
 *   "DEBUG - JS injected" badge below their header-info badge area, making it
 *   easy to visually distinguish Liquid-rendered slides from JS-injected ones
 *   during development.
 *
 * ARN_DEBUG_LOGS — when false, ARN_LOG (info-level) calls are silenced.
 *   ARN_WARN and ARN_ERR also respect this flag in the current implementation;
 *   flip the flag during development to see the full picture.
 */
const ARN_DEBUG_BADGES = window.arnPlpConfig?.engine?.debugBadges ?? false;
const ARN_DEBUG_LOGS = window.arnPlpConfig?.engine?.debugLogs ?? false;

const ARN_LOG = (...a) => { if (ARN_DEBUG_LOGS) console.log('[ARN PLP]', ...a); };
const ARN_WARN = (...a) => { if (ARN_DEBUG_LOGS) console.warn('[ARN PLP] ⚠️', ...a); };
const ARN_ERR = (...a) => { if (ARN_DEBUG_LOGS) console.error('[ARN PLP] ❌', ...a); };

// #endregion

// #region Helpers
// ════════════════════════════════════════════════════════════════════════════

/**
 * Promise-based timeout. Used to insert short pauses between render chunks
 * to give Shopify's section-render rate limiter breathing room.
 */
const sleep = ms => new Promise(r => setTimeout(r, ms));

/**
 * Extract the product handle from a Shopify product URL.
 *
 *   /collections/foo/products/bar?variant=123  →  "bar"
 *   /products/bar#size-m                       →  "bar"
 *   /products/bar?variant=1#hash               →  "bar"
 *
 * Returns null when no handle can be parsed. Strips both query string and
 * hash so anchored variant pickers (e.g. "/products/foo#variant=1") don't
 * leak the fragment into the handle.
 */
function _handleFromUrl(href) {
  if (!href) return null;
  const parts = href.split('/');
  const last = parts[parts.length - 1];
  if (!last) return null;
  // Strip query string first, then hash. Order matters because a URL can
  // contain both, and `?` always precedes `#` per RFC 3986.
  const noQuery = last.split('?')[0];
  const noHash = noQuery.split('#')[0];
  return noHash || null;
}

/**
 * Normalize a product handle before comparing Storefront and Liquid output.
 * Shopify handles are lowercase, but ST/metafield payloads are normalized
 * defensively so casing cannot change the collection rank.
 */
function normalizeMocoHandle(handle) {
  return String(handle || '').trim().toLowerCase();
}

/**
 * Stable key used to group the manual collection sequence by model.
 *
 * Intentionally coarser than plpCardGroupKey: the model-wide handle list is a
 * superset of every card's colors and orderMocoHandlesByCollection only ranks
 * the handles a card renders, so the color order is the same. Switching this to
 * plpCardGroupKey would also require groupKey in COLLECTION_MOCO_ORDER_QUERY.
 */
function collectionMocoModelKey(product) {
  return String(product?.modelCode?.value || product?.handle || '').trim().toLowerCase();
}

/**
 * Card identity in grouped mode: one card per model code + PIM stylename
 * (custom.x_groupkey), the same groups PIM uses to build variantshandles.
 * "Raven" and "Raven - Exclusive" share model code AN4368 but are two cards.
 *
 *   - blank x_groupkey → model code only
 *   - blank model code → handle
 *   - case-sensitive on purpose: PIM keeps e.g. "Clayface" and "CLAYFACE" as
 *     separate variantshandles groups, folding case would hide one of them
 *
 * Nodes must come from COLLECTION_PRODUCTS_QUERY (groupKey alias).
 * Keep in sync with the Liquid fallback in arn-main-collection-product-grid.liquid.
 */
function plpCardGroupKey(product) {
  const modelCode = String(product?.modelCode?.value || '').trim();
  if (!modelCode) return product?.handle || '';
  const styleName = String(product?.groupKey?.value || '').trim();
  return styleName ? JSON.stringify([modelCode, styleName]) : modelCode;
}

/**
 * Accumulate each model's moco handles in the order returned by the manual
 * collection scan. The same map is reused across cursor pages.
 */
function buildCollectionMocoOrderByModel(products, orderByModel = new Map()) {
  for (const product of products || []) {
    const modelKey = collectionMocoModelKey(product);
    const handle = normalizeMocoHandle(product?.handle);
    if (!modelKey || !handle) continue;

    const orderedHandles = orderByModel.get(modelKey) || [];
    if (!orderedHandles.includes(handle)) {
      orderedHandles.push(handle);
      orderByModel.set(modelKey, orderedHandles);
    }
  }

  return orderByModel;
}

/**
 * Rank rendered PLP colors by their ST-owned collection position. A rendered
 * handle that is not part of the collection remains after ranked colors and
 * keeps its previous VariantHandles-relative position as a safe fallback.
 *
 * Unavailable colors are always pushed after the available ones so the first
 * visible card slide is never sold out; the collection sequence is preserved
 * inside each of the two groups.
 */
function orderMocoHandlesByCollection(renderedHandles, collectionHandles, unavailableHandles) {
  const collectionRank = new Map(
    (collectionHandles || []).map((handle, index) => [normalizeMocoHandle(handle), index])
  );

  return (renderedHandles || [])
    .map((handle, originalIndex) => {
      const normalizedHandle = normalizeMocoHandle(handle);
      return {
        handle: normalizedHandle,
        originalIndex,
        unavailable: unavailableHandles?.has(normalizedHandle) ? 1 : 0,
        collectionIndex: collectionRank.has(normalizedHandle)
          ? collectionRank.get(normalizedHandle)
          : Number.MAX_SAFE_INTEGER,
      };
    })
    .sort((left, right) =>
      left.unavailable - right.unavailable
      || left.collectionIndex - right.collectionIndex
      || left.originalIndex - right.originalIndex
    )
    .map(({ handle }) => handle);
}

// #endregion

// #region Storefront API Client
// ════════════════════════════════════════════════════════════════════════════

/**
 * GraphQL query for paginated collection products. Fetches:
 *   - Identity: handle
 *   - Availability: availableForSale
 *   - Card grouping key: modelCode + groupKey metafields (see plpCardGroupKey)
 *   - Sibling resolution key: variantsHandles metafield
 *   - Tealium tracking data: first 5 variants with id, barcode, price,
 *     compareAtPrice, plus the product-level "disco" metafield
 *
 * The variants(first: 10) buffer must cover ALL variants of a product: RX
 * clones double the size-variant count (e.g. 64-18/64-18RX), and both the
 * Tealium tracker and the price-sort key select the first availableForSale
 * variant (falling back to the first one) to mirror Liquid's
 * `product.selected_or_first_available_variant` — a truncated list can pick
 * a different variant than the rendered card and desync the sort order.
 *
 * The @inContext directive scopes the query by market so cached results
 * don't leak across countries/languages on Shopify's edge layer.
 */
const COLLECTION_PRODUCTS_QUERY = /* GraphQL */ `
  query CollectionProducts(
    $handle: String!, $first: Int!, $after: String,
    $filters: [ProductFilter!], $sortKey: ProductCollectionSortKeys, $reverse: Boolean,
    $country: CountryCode!, $language: LanguageCode!
  ) @inContext(country: $country, language: $language) {
    collection(handle: $handle) {
      products(first: $first, after: $after, filters: $filters, sortKey: $sortKey, reverse: $reverse) {
        edges {
          node {
            handle
            availableForSale
            modelCode: metafield(namespace: "custom", key: "model_code_display") { value }
            groupKey: metafield(namespace: "custom", key: "x_groupkey") { value }
            variantsHandles: metafield(namespace: "custom", key: "variantshandles") { value }
            discoMetafield: metafield(namespace: "custom", key: "disco") { value }
            categoryMetafield: metafield(namespace: "custom", key: "prod_hierarchy_1") { value }
            productType
            variants(first: 10) {
              edges {
                node {
                  id
                  availableForSale
                  barcode
                  sku
                  discoMetafield: metafield(namespace: "custom", key: "disco") { value }
                  price { amount }
                  compareAtPrice { amount }
                }
              }
            }
          }
        }
        pageInfo { hasNextPage endCursor }
      }
    }
  }
`;

/**
 * Lean, unfiltered scan of the collection's manual order. ST writes its PLP
 * ranking to this sequence, so it must stay independent from shopper-selected
 * filters and sort options. Only handle + model code are requested to keep a
 * 250-product page well below the Storefront query-complexity budget.
 */
const COLLECTION_MOCO_ORDER_QUERY = /* GraphQL */ `
  query CollectionMocoOrder(
    $handle: String!, $first: Int!, $after: String,
    $country: CountryCode!, $language: LanguageCode!
  ) @inContext(country: $country, language: $language) {
    collection(handle: $handle) {
      products(first: $first, after: $after, sortKey: MANUAL) {
        edges {
          node {
            handle
            modelCode: metafield(namespace: "custom", key: "model_code_display") { value }
          }
        }
        pageInfo { hasNextPage endCursor }
      }
    }
  }
`;

// Like COLLECTION_PRODUCTS_QUERY but with price ranges + DK discount references,
// for final-price sorting. DK "discounts" is a list.metaobject_reference, so the
// objects must be read from `references` (Storefront `value` only yields GIDs).
const COLLECTION_PRODUCTS_PRICED_QUERY = /* GraphQL */ `
  query CollectionProductsPriced(
    $handle: String!, $first: Int!, $after: String,
    $filters: [ProductFilter!], $sortKey: ProductCollectionSortKeys, $reverse: Boolean,
    $country: CountryCode!, $language: LanguageCode!
  ) @inContext(country: $country, language: $language) {
    collection(handle: $handle) {
      products(first: $first, after: $after, filters: $filters, sortKey: $sortKey, reverse: $reverse) {
        edges {
          node {
            handle
            availableForSale
            modelCode: metafield(namespace: "custom", key: "model_code_display") { value }
            variantsHandles: metafield(namespace: "custom", key: "variantshandles") { value }
            discoMetafield: metafield(namespace: "custom", key: "disco") { value }
            categoryMetafield: metafield(namespace: "custom", key: "prod_hierarchy_1") { value }
            productType
            variants(first: 10) {
              edges {
                node {
                  id
                  availableForSale
                  barcode
                  sku
                  discoMetafield: metafield(namespace: "custom", key: "disco") { value }
                  price { amount }
                  compareAtPrice { amount }
                }
              }
            }
            priceRange { minVariantPrice { amount } }
            compareAtPriceRange { minVariantPrice { amount } }
            dkDiscounts: metafield(namespace: "app--9549316097--discount_kit", key: "discounts") {
              references(first: 10) { nodes { ... on Metaobject { fields { key value } } } }
            }
            dkMaxReward: metafield(namespace: "app--9549316097--discount_kit", key: "max_reward_discount") {
              reference { ... on Metaobject { fields { key value } } }
            }
          }
        }
        pageInfo { hasNextPage endCursor }
      }
    }
  }
`;

/**
 * Thin wrapper around the Storefront API. Handles authentication, error
 * surfacing, and batched lookups by handle.
 *
 * Note: the Storefront API has no points-based rate limit (unlike the Admin
 * API), so batched lookups are an optimisation purely for HTTP latency —
 * the API cost is identical to N separate single-product queries.
 *
 * The Storefront token is intentionally public — it is scoped to read-only
 * storefront operations and is safe to embed in client-side code per Shopify's
 * security model. The constructor validates both url and token at boot so
 * configuration mistakes fail loudly instead of degrading silently into 404s.
 */
class ArnPlpStorefrontClient {
  constructor({ url, token } = {}) {
    if (!url || typeof url !== 'string') {
      throw new Error(
        'ArnPlpStorefrontClient: missing Storefront API url. ' +
        'Expected arnPlpConfig.storefront.url or window.storefront.url to be defined.'
      );
    }
    if (!token || typeof token !== 'string') {
      throw new Error(
        'ArnPlpStorefrontClient: missing Storefront API token. ' +
        'Expected arnPlpConfig.storefront.token or window.storefront.token to be defined.'
      );
    }
    this._url = url;
    this._token = token;
  }

  async query(query, variables = {}) {
    const res = await fetch(this._url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this._token,
      },
      body: JSON.stringify({ query, variables }),
    });
    if (!res.ok) throw new Error(`Storefront API HTTP ${res.status}`);
    const json = await res.json();
    if (json.errors?.length) throw new Error(`Storefront API: ${json.errors[0].message}`);
    return json.data;
  }

  /**
   * Fetch a single page of collection products with filters, sort, and
   * cursor pagination. Used by ArnLoadMore to walk the collection until
   * TARGET_CARDS_PER_LOAD cards are filled.
   */
  async fetchCollectionPage(handle, cursor, filters, sortKey, reverse) {
    ARN_LOG(`fetchCollectionPage — handle: ${handle}, cursor: ${cursor}, sortKey: ${sortKey}, reverse: ${reverse}, filters:`, filters);
    ARN_LOG(`🌍 Storefront API context — market: "${ARN_MARKET.handle}", country: ${ARN_MARKET.country}, language: ${ARN_MARKET.language}`);

    const data = await this.query(COLLECTION_PRODUCTS_QUERY, {
      handle,
      first: ARN_CFG.PRODUCTS_PER_FETCH,
      after: cursor || null,
      filters: filters.length ? filters : [],
      sortKey: sortKey || 'COLLECTION_DEFAULT',
      reverse: !!reverse,
      language: ARN_MARKET.language || 'EN',
      country: ARN_MARKET.country || 'US'
    });
    const col = data?.collection;
    if (!col) {
      ARN_WARN('fetchCollectionPage — no collection returned');
      return { products: [], hasNextPage: false, endCursor: null };
    }
    const products = col.products.edges.map(e => e.node);
    ARN_LOG(`fetchCollectionPage — got ${products.length} products, hasNextPage: ${col.products.pageInfo.hasNextPage}`);
    return {
      products,
      hasNextPage: col.products.pageInfo.hasNextPage,
      endCursor: col.products.pageInfo.endCursor,
    };
  }

  /**
   * Fetch one page of the unfiltered manual collection order written by ST.
   * This deliberately uses a dedicated lean query rather than the richer card
   * query, because the complete collection must be scanned before card assembly.
   */
  async fetchCollectionMocoOrderPage(handle, cursor) {
    const data = await this.query(COLLECTION_MOCO_ORDER_QUERY, {
      handle,
      first: 250,
      after: cursor || null,
      language: ARN_MARKET.language || 'EN',
      country: ARN_MARKET.country || 'US',
    });
    const col = data?.collection;
    if (!col) {
      ARN_WARN('fetchCollectionMocoOrderPage — no collection returned');
      return { products: [], hasNextPage: false, endCursor: null };
    }
    return {
      products: col.products.edges.map(e => e.node),
      hasNextPage: col.products.pageInfo.hasNextPage,
      endCursor: col.products.pageInfo.endCursor,
    };
  }

  // Like fetchCollectionPage but with the priced query (DK refs + price ranges).
  async fetchCollectionPagePriced(handle, cursor, filters, sortKey, reverse, first) {
    const data = await this.query(COLLECTION_PRODUCTS_PRICED_QUERY, {
      handle,
      first: first || ARN_CFG.PRODUCTS_PER_FETCH,
      after: cursor || null,
      filters: filters.length ? filters : [],
      sortKey: sortKey || 'COLLECTION_DEFAULT',
      reverse: !!reverse,
      language: ARN_MARKET.language || 'EN',
      country: ARN_MARKET.country || 'US',
    });
    const col = data?.collection;
    if (!col) {
      ARN_WARN('fetchCollectionPagePriced — no collection returned');
      return { products: [], hasNextPage: false, endCursor: null };
    }
    return {
      products: col.products.edges.map(e => e.node),
      hasNextPage: col.products.pageInfo.hasNextPage,
      endCursor: col.products.pageInfo.endCursor,
    };
  }

  /**
   * Fetch multiple products in a single GraphQL query using field aliases.
   *
   * The query is composed by aliasing each `product(handle: ...)` lookup:
   *
   *   query BatchProducts {
   *     p0: product(handle: "handle-a") { … }
   *     p1: product(handle: "handle-b") { … }
   *     …
   *   }
   *
   * Handles are split into SIBLING_BATCH_SIZE chunks to keep individual
   * GraphQL payloads small — large alias queries can hit undocumented
   * complexity ceilings.
   *
   * Returns a flat map of { handle → productNode }. Handles not found in
   * the store are absent from the returned map (callers treat a missing
   * key the same as an explicit null).
   *
   * The field set mirrors COLLECTION_PRODUCTS_QUERY so siblings carry the
   * same Tealium tracking data (variants/barcode/price) as primaries.
   */
  async fetchProductsByHandles(handles) {
    if (!handles.length) return {};

    const result = {};

    for (let i = 0; i < handles.length; i += ARN_CFG.SIBLING_BATCH_SIZE) {
      const batch = handles.slice(i, i + ARN_CFG.SIBLING_BATCH_SIZE);

      const aliasedFields = batch
        .map((h, idx) => `
          p${idx}: product(handle: ${JSON.stringify(h)}) {
            handle
            availableForSale
            modelCode: metafield(namespace: "custom", key: "model_code_display") { value }
            variantsHandles: metafield(namespace: "custom", key: "variantshandles") { value }
            discoMetafield: metafield(namespace: "custom", key: "disco") { value }
            categoryMetafield: metafield(namespace: "custom", key: "prod_hierarchy_1") { value }
            productType
            variants(first: 10) {
              edges {
                node {
                  id
                  availableForSale
                  barcode
                  sku
                  discoMetafield: metafield(namespace: "custom", key: "disco") { value }
                  price { amount }
                  compareAtPrice { amount }
                }
              }
            }
          }
        `)
        .join('\n');

      ARN_LOG(`fetchProductsByHandles — batch ${Math.floor(i / ARN_CFG.SIBLING_BATCH_SIZE) + 1}: fetching ${batch.length} handle(s) in one query`);

      const data = await this.query(`query BatchProducts { ${aliasedFields} }`);

      for (const node of Object.values(data)) {
        if (node?.handle) {
          result[node.handle] = node;
        }
        // null entries (product not found) are intentionally not stored —
        // callers treat a missing key the same as an explicit null.
      }
    }

    ARN_LOG(`fetchProductsByHandles — resolved ${Object.keys(result).length}/${handles.length} handle(s)`);
    return result;
  }
}

// #endregion

// #region URL Parser
// ════════════════════════════════════════════════════════════════════════════

/**
 * Translates Shopify storefront filter URL params into the Storefront API
 * ProductFilter shape, and reads sort_by into the SortKey enum + reverse flag.
 *
 * Also exposes a couple of state predicates used by the engine to decide
 * whether grouping is allowed and whether media blocks should be shown.
 */
class ArnPlpUrlParser {
  static buildFilters() {
    const params = new URLSearchParams(window.location.search);
    const filters = [];
    const price = {};
    for (const [k, v] of params.entries()) {
      if (!k.startsWith('filter.')) continue;

      // Availability
      if (k === 'filter.v.availability') {
        filters.push({ available: v === '1' || v === 'true' });
        continue;
      }

      // Tag
      if (k === 'filter.p.tag') {
        filters.push({ tag: v });
        continue;
      }

      if (k === 'filter.p.product_type') {
        filters.push({ productType: v });
        continue;
      }

      // Product metafields: filter.p.m.<namespace>.<key>
      const pmf = k.match(/^filter\.p\.m\.([^.]+)\.(.+)$/);
      if (pmf) {
        filters.push({ productMetafield: { namespace: pmf[1], key: pmf[2], value: v } });
        continue;
      }

      // Variant metafields: filter.v.m.<namespace>.<key>
      const vmf = k.match(/^filter\.v\.m\.([^.]+)\.(.+)$/);
      if (vmf) {
        filters.push({ variantMetafield: { namespace: vmf[1], key: vmf[2], value: v } });
        continue;
      }

      // Price range
      if (k === 'filter.v.price.gte') { price.min = parseFloat(v); continue; }
      if (k === 'filter.v.price.lte') { price.max = parseFloat(v); continue; }

      // Variant options: filter.v.option.<option_name>
      const opt = k.match(/^filter\.v\.option\.(.+)$/);
      if (opt) {
        filters.push({ variantOption: { name: opt[1], value: v } });
        continue;
      }

      ARN_WARN(`buildFilters — unrecognized filter param ignored: ${k}=${v}`);
    }
    if (Object.keys(price).length) filters.push({ price });
    if (filters.length) ARN_LOG(`Active filters: ${JSON.stringify(filters)}`);
    return filters;
  }

  static buildSort() {
    const s = new URLSearchParams(window.location.search).get('sort_by') || 'manual';
    const map = {
      'manual': { sortKey: 'COLLECTION_DEFAULT', reverse: false },
      'best-selling': { sortKey: 'BEST_SELLING', reverse: false },
      'title-ascending': { sortKey: 'TITLE', reverse: false },
      'title-descending': { sortKey: 'TITLE', reverse: true },
      'price-ascending': { sortKey: 'PRICE', reverse: false },
      'price-descending': { sortKey: 'PRICE', reverse: true },
      'created-ascending': { sortKey: 'CREATED', reverse: false },
      'created-descending': { sortKey: 'CREATED', reverse: true },
    };
    return map[s] || map['manual'];
  }

  static isPriceSorting() {
    const s = new URLSearchParams(window.location.search).get('sort_by') || '';
    return s === 'price-ascending' || s === 'price-descending';
  }

  static isPriceSortDescending() {
    return (new URLSearchParams(window.location.search).get('sort_by') || '') === 'price-descending';
  }

  /**
   * Returns true when grouping must be disabled entirely — i.e. every product
   * must be shown as an individual single-slide card with no sibling injection.
   *
   * Currently this covers price sorting, where Shopify interleaves siblings at
   * different price points and grouping them would show the wrong price order.
   * Liquid also renders all output_variants for the primary product when
   * disable_grouping is NOT set, so we must pass disable_grouping=1 to the
   * section render as well — not just skip JS sibling injection.
   */
  static isGroupingDisabled() {
    return ArnPlpUrlParser.isPriceSorting();
  }

  static hasActiveFilters() {
    for (const k of new URLSearchParams(window.location.search).keys()) {
      if (k.startsWith('filter.')) return true;
    }
    return false;
  }
}

// #endregion

// #region Final-Price Resolver
// ════════════════════════════════════════════════════════════════════════════

// Final discounted price per product so price sorting matches the card price,
// not the raw Storefront base price. Reproduces the cascade in
// component.product-price.liquid and delegates the eligibility/sale math to
// window.ArnDkStorefrontPrices (the same module that patches the card prices).
class ArnPlpFinalPrice {
  static isAvailable() {
    return typeof window.ArnDkStorefrontPrices?.bestDkSaleCents === 'function';
  }

  static dkCtx() {
    const ctx = window.arnDkStorefrontContext || {};
    return {
      market: String(ctx.market || '').toLowerCase().trim(),
      currency: String(ctx.currency || '').toUpperCase().trim(),
      appliedCodes: window.ArnDkStorefrontPrices?.getAppliedCodesCsv?.() || '',
      customerHasTestTag: Boolean(ctx.customerHasTestTag),
      customerLoggedIn: Boolean(ctx.customerLoggedIn),
      cartAttributes: window.ArnDkStorefrontPrices?.getPromoAttributes?.() || {},
    };
  }

  // Coerce metaobject field strings into the shapes the DK cascade expects
  // (booleans, and JSON like included_markets "[]" or config "{…}").
  static _coerceField(v) {
    if (v == null) return v;
    const s = String(v).trim();
    if (s === 'true') return true;
    if (s === 'false') return false;
    if (s && (s[0] === '[' || s[0] === '{')) {
      try { return JSON.parse(s); } catch { /* keep raw string */ }
    }
    return v;
  }

  static _metaobjectToDiscount(mo) {
    const out = {};
    for (const f of mo?.fields || []) out[f.key] = ArnPlpFinalPrice._coerceField(f.value);
    return out;
  }

  static _perProductDiscounts(node) {
    const refs = node?.dkDiscounts?.references?.nodes || [];
    return refs
      .map((mo) => ArnPlpFinalPrice._metaobjectToDiscount(mo))
      .filter((d) => Object.keys(d).length);
  }

  static _maxDiscount(node) {
    const ref = node?.dkMaxReward?.reference;
    if (!ref) return null;
    const d = ArnPlpFinalPrice._metaobjectToDiscount(ref);
    return Object.keys(d).length ? d : null;
  }

  // Mirrors resolveDiscountsFromDataset: per-product + universal shop, else max_reward.
  static _discounts(node) {
    const perProduct = ArnPlpFinalPrice._perProductDiscounts(node);
    const universal = window.ArnDkStorefrontPrices?.getUniversalShopDiscounts?.() || [];
    if (perProduct.length || universal.length) return perProduct.concat(universal);
    const max = ArnPlpFinalPrice._maxDiscount(node);
    return max ? [max] : [];
  }

  // Mirrors Liquid's selected_or_first_available_variant (same rule as
  // ArnPlpTealiumTracker._pickRepresentativeVariant): first available
  // variant, else first. Null when the node carries no variants.
  static _representativeVariant(node) {
    const edges = node?.variants?.edges;
    if (!edges || !edges.length) return null;
    const variants = edges.map((e) => e.node);
    return variants.find((v) => v.availableForSale) || variants[0] || null;
  }

  static cents(node, ctx) {
    // The card displays the selected/first-available variant's price, not the
    // product minimum — with RX-clone variants (e.g. "64-18RX") the minimum is
    // the clone's price, so sorting by it would put cards out of price order.
    // Score with the same variant the card shows; fall back to the range
    // minimums only when the node carries no variants.
    const variant = ArnPlpFinalPrice._representativeVariant(node);
    const baseCents = variant
      ? Math.round(parseFloat(variant.price?.amount || '0') * 100)
      : Math.round(parseFloat(node?.priceRange?.minVariantPrice?.amount || '0') * 100);
    if (!baseCents) return 0;

    // Manual/native sale: the base price is already the sale price, DK ignored.
    const compareCents = variant
      ? Math.round(parseFloat(variant.compareAtPrice?.amount || '0') * 100)
      : Math.round(parseFloat(node?.compareAtPriceRange?.minVariantPrice?.amount || '0') * 100);
    if (compareCents > baseCents) return baseCents;

    const discounts = ArnPlpFinalPrice._discounts(node);
    if (!discounts.length || typeof window.ArnDkStorefrontPrices?.bestDkSaleCents !== 'function') {
      return baseCents;
    }

    const best = window.ArnDkStorefrontPrices.bestDkSaleCents(baseCents, discounts, ctx);
    return best?.hasDiscount ? best.saleCents : baseCents;
  }
}

/**
 * Walk every cursor page of a collection query, dropping duplicate handles.
 *
 * Price-sorted Storefront cursors repeat and skip products, so callers that
 * need a complete ordered set must walk with a stable sort key (manual /
 * collection default) and order the result themselves.
 *
 * @param {(cursor: string|null) => Promise<{products: object[], hasNextPage: boolean, endCursor: string|null}>} fetchPage
 * @returns {Promise<{products: object[], pages: number}>}
 */
async function walkCollectionPages(fetchPage) {
  const products = [];
  const seen = new Set();
  let cursor = null;
  let hasNext = true;
  let pages = 0;

  while (hasNext) {
    let page;
    try {
      page = await fetchPage(cursor);
    } catch (err) {
      ARN_ERR('walkCollectionPages — page fetch failed; using partial result:', err);
      break;
    }
    for (const product of page.products || []) {
      const handle = normalizeMocoHandle(product.handle);
      if (!handle || seen.has(handle)) continue;
      seen.add(handle);
      products.push(product);
    }

    pages += 1;
    const nextCursor = page.endCursor || null;
    if (!page.hasNextPage || !page.products?.length || (nextCursor && nextCursor === cursor)) {
      hasNext = false;
    } else {
      cursor = nextCursor;
      if (ARN_CFG.RENDER_CHUNK_DELAY_MS > 0) await sleep(ARN_CFG.RENDER_CHUNK_DELAY_MS);
    }
  }

  return { products, pages };
}

// #endregion

// #region Skeleton Manager
// ════════════════════════════════════════════════════════════════════════════

/**
 * Owns the lifecycle of skeleton placeholder cells in the grid.
 *
 * Skeletons serve two purposes:
 *   1. Reserve grid positions while real cards are being assembled in
 *      memory, preventing layout shift when the real card is committed.
 *   2. Provide visible shimmer feedback during fetches, so the user never
 *      sees an empty grid.
 *
 * ── Visibility model ───────────────────────────────────────────────────
 * Skeletons are ALWAYS visible from the moment they enter the DOM. The
 * fixed aspect-ratio defined in arn-plp-product-card.css gives them a
 * stable cell size independent of any real card, so no "height reference"
 * delay is needed. This keeps the visual experience smooth: the user sees
 * a uniform shimmer grid throughout the load, never a flicker between
 * partial and full coverage.
 *
 * The "pending queue" pattern lets pre-inserted skeletons be adopted by
 * the render pipeline. When ArnLoadMore knows a batch is starting (initial
 * load or Load More click), it asks the manager to pre-fill the grid with
 * skeletons immediately. As real cards become available, the renderer
 * pops skeletons from the queue and replaces them in place.
 */
class ArnPlpSkeletonManager {
  constructor(grid) {
    this._grid = grid;
    this._pending = [];
  }

  /**
   * Create a single skeleton <li>. Always visible — the fixed aspect-ratio
   * in arn-plp-product-card.css provides a stable cell size so we don't
   * need any reveal-after-first-card dance.
   *
   * NOTE: do NOT put arn-product-card-slider-component here — the skeleton
   * must never trigger Swiper initialisation.
   */
  create() {
    const li = document.createElement('li');
    li.setAttribute('data-skeleton', '');
    li.innerHTML = `
      <div class="arn-product-card tw-relative tw-h-full" data-card-ready="false">
        <div class="arn-skeleton-sizer tw-animate-pulse tw-bg-arn-secondary dark:tw-bg-arn-dark-secondary tw-outline tw-outline-1 tw-outline-arn-default dark:tw-outline-arn-dark-default tw-w-full">
        </div>
      </div>`;
    return li;
  }

  /**
   * Pre-insert N skeletons into the grid before any fetch starts.
   * The created skeletons go on the pending queue, ready to be adopted
   * by the next render cycle.
   */
  preFill(count) {
    ARN_LOG(`SkeletonManager.preFill — inserting ${count} skeleton(s) upfront`);
    for (let i = 0; i < count; i++) {
      const sk = this.create();
      this._grid.appendChild(sk);
      this._pending.push(sk);
    }
  }

  /**
   * Pop one pending skeleton from the queue. When the queue is empty, a
   * new skeleton is created and appended to the grid (this happens when a
   * batch yields more groups than were pre-filled — rare but possible).
   */
  takeOrCreate() {
    if (this._pending.length > 0) {
      return this._pending.shift();
    }
    const sk = this.create();
    this._grid.appendChild(sk);
    return sk;
  }

  /**
   * Remove all leftover pre-inserted skeletons. Used at the end of a render
   * batch when fewer real cards were produced than pre-filled skeletons.
   * Also called when transitioning to the empty state so no ghost cells
   * remain visible.
   */
  drainPending() {
    if (!this._pending.length) return;
    ARN_LOG(`SkeletonManager.drainPending — removing ${this._pending.length} unused skeleton(s)`);
    this._pending.forEach(sk => sk.remove());
    this._pending = [];
  }
}

// #endregion

// #region Card Renderer (Section Rendering API + assembly)
// ════════════════════════════════════════════════════════════════════════════

/**
 * Maps a data-zone value from the unified variant fragment to the selector
 * for the corresponding destination container inside the primary card.
 *
 * The fragment from arn-plp-slide-renderer.liquid contains one element per
 * zone, each tagged `data-zone="<zone-name>"`. The JS distributes each piece
 * into the matching wrapper inside the primary card. This replaces the
 * previous mechanism that required 5 separate fragment containers in the
 * Liquid output and 5 separate handling paths in the JS.
 */
const VARIANT_ZONE_TARGETS = {
  'header-info': '.header-info-wrapper',
  'wishlist': '.wishlist-wrapper',
  'slide-image': '.swiper-wrapper',
  'color-display': '[data-color-display-wrapper]',
  'product-info': '.arn-product-info',
};

/**
 * Selectors for the four zone wrappers whose direct children carry the
 * uniform per-variant index. Listed once here so _assignFinalIndices stays
 * in lock-step with VARIANT_ZONE_TARGETS without duplicating selector logic.
 * Note: the swiper-wrapper is handled separately because slides also need
 * their `id` updated.
 */
const VARIANT_INDEX_ZONE_SELECTORS = [
  '.header-info-wrapper',
  '.wishlist-wrapper',
  '[data-color-display-wrapper]',
  '.arn-product-info',
];

/**
 * Fetches and assembles product cards via Shopify's Section Rendering API.
 *
 * For each card, the renderer:
 *   1. Fetches the primary product card HTML from arn-plp-product-card-renderer.
 *   2. Fetches sibling variant fragments in parallel from arn-plp-slide-renderer
 *      (one fetch per missing sibling).
 *   3. Distributes each fragment's data-zone pieces into the matching
 *      containers of the primary card.
 *   4. Reorders every variant zone by ST's manual collection sequence, with
 *      the previous availability/VariantHandles order as a degraded fallback.
 *   5. Assigns a single data-variant-index uniformly across all zones, so
 *      the CSS in arn-plp-product-card.css can drive visibility.
 *
 * The "Swiper-pending" guard (see fetchAndAssembleCard JSDoc) is the trickiest
 * part of this class — it prevents Swiper from initialising on detached cards
 * before they have layout dimensions.
 */
class ArnPlpCardRenderer {
  constructor(collectionHandle, skeletonManager) {
    this._col = collectionHandle;
    this._skeletons = skeletonManager;
  }

  // ── Card display settings (params + DOM strip) ──────────────────────────────

  /**
   * Build query params string for card display settings, derived from
   * arnPlpConfig.card (which reflects the PLP grid section settings).
   * Only params that differ from the default (true) are appended, keeping
   * URLs short in the common case where everything is enabled.
   *
   * NOTE: these params are sent as a best-effort hint to the Liquid renderer.
   * Shopify's Section Rendering API does not guarantee that custom query params
   * are available via request.parameters in all caching scenarios.
   * _applyCardSettings() performs a client-side DOM strip as the reliable
   * fallback, operating on the parsed HTML before it reaches the live DOM.
   */
  _cardSettingsParams() {
    const cfg = window.arnPlpConfig?.card || {};
    let params = '';
    if (cfg.showWishlistIcon === false) params += '&show_wishlist_icon=0';
    if (cfg.showPrice === false) params += '&show_price=0';
    if (cfg.showVatIncludedLabel === false) params += '&show_vat_included_label=0';
    return params;
  }

  /**
   * Strip card elements that are disabled in arnPlpConfig.card.
   *
   * This is the reliable client-side counterpart to the Liquid query-param
   * approach. Because Shopify's Section Rendering API may serve cached HTML
   * that ignores custom query params, we always enforce the settings here by
   * removing the relevant DOM nodes from the parsed document BEFORE it is
   * inserted into the live DOM.
   *
   * Operates on a detached document or <li> element — never on the live DOM —
   * so removal is cheap and has no layout side-effects. The selectors are a
   * superset that match both full-card and variant-fragment markup, so the
   * same method is safely called on both kinds of input.
   */
  _applyCardSettings(root) {
    const cfg = window.arnPlpConfig?.card || {};

    // Wishlist — .wishlist-wrapper exists in full cards, .wishlist-button-item
    // exists in variant fragments; both selectors run safely on either input.
    if (cfg.showWishlistIcon === false) {
      root.querySelectorAll('.wishlist-wrapper').forEach(el => el.remove());
      root.querySelectorAll('.wishlist-button-item').forEach(el => el.remove());
      ARN_LOG('_applyCardSettings — wishlist removed');
    }

    // Price (also removes VAT label which lives inside arn-product-price)
    if (cfg.showPrice === false) {
      root.querySelectorAll('arn-product-price').forEach(el => el.remove());
      ARN_LOG('_applyCardSettings — price removed');
    }

    // VAT label — only when showPrice is true but showVatIncludedLabel is false
    if (cfg.showPrice !== false && cfg.showVatIncludedLabel === false) {
      root.querySelectorAll('[data-arn-vat]').forEach(el => el.remove());
      ARN_LOG('_applyCardSettings — VAT label removed');
    }
  }

  /**
   * Build a small debug badge element appended inside the header-info-item
   * of each JS-injected variant, making it visually identifiable during
   * development.
   *
   * Controlled by ARN_DEBUG_BADGES, which is driven by the section setting
   * debug_js_injected_badge in arn-main-collection-product-grid.liquid.
   */
  _buildDebugBadge() {
    if (!ARN_DEBUG_BADGES) return null;
    const badge = document.createElement('div');
    badge.style.cssText = [
      'display:inline-block',
      'font-family:monospace',
      'font-size:9px',
      'line-height:1.4',
      'padding:1px 4px',
      'border:1px solid currentColor',
      'opacity:0.5',
      'margin-top:2px',
      'pointer-events:none',
    ].join(';');
    badge.textContent = 'DEBUG - JS injected';
    return badge;
  }

  // ── Section Rendering API fetches ──────────────────────────────────────────

  /**
   * Low-level fetch with cache busting and degradation detection.
   * Section Rendering API responses are sometimes served from a stale cache
   * or as a full-page redirect — we detect both and emit a warning so the
   * issue can be diagnosed during development.
   */
  async _fetchSection(url, label) {
    const bustUrl = `${url}&_cb=${Date.now()}`;
    try {
      const res = await fetch(bustUrl, {
        headers: { 'X-Requested-With': 'XMLHttpRequest', 'Cache-Control': 'no-cache' },
        cache: 'no-store',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();

      const hasSectionOutput = html.includes('arn-card-renderer-output') || html.includes('arn-variant-fragment');
      const looksLikeFullPage = html.includes('<html') || html.includes('<!DOCTYPE');
      const isShort = html.length < 500;

      if (!hasSectionOutput || looksLikeFullPage || isShort) {
        ARN_WARN(`Section render degraded for "${label}"`, {
          url: bustUrl, htmlLength: html.length, hasSectionOutput, looksLikeFullPage, isShort,
          preview: html.slice(0, 800),
        });
      }

      return html;
    } catch (err) {
      ARN_ERR(`Section render fetch failed for "${label}":`, err);
      return null;
    }
  }

  /**
   * Parse section-render HTML and return the #arn-card-renderer-output
   * innerHTML as a raw string, WITHOUT ever touching a live document.
   * Parsing happens entirely inside DOMParser so no custom elements are
   * upgraded and no Swiper instances are created at this stage.
   *
   * Memory: by returning a string (not a node) the parser Document becomes
   * unreferenced as soon as this method returns, allowing immediate GC.
   */
  async _fetchFullCard(handle, disableGrouping = false) {
    let url = `${Shopify.routes.root}collections/${this._col}/products/${handle}?section_id=arn-plp-product-card-renderer`;
    if (disableGrouping) url += '&disable_grouping=1';
    url += this._cardSettingsParams();

    const html = await this._fetchSection(url, handle);
    if (!html) return null;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const out = doc.getElementById('arn-card-renderer-output');
    if (!out) {
      ARN_ERR(`Full card for "${handle}" — #arn-card-renderer-output not found in response. Preview:`, html.slice(0, 500));
      return null;
    }

    const slideCount = out.querySelectorAll('.swiper-slide').length;
    ARN_LOG(`  Card HTML fetched for "${handle}" — Liquid rendered ${slideCount} slide(s)`);

    // Strip disabled elements (wishlist, price, VAT) according to arnPlpConfig.card
    // on the detached DOMParser document before serialising back to HTML, so the
    // live DOM never sees the unwanted nodes regardless of Liquid caching.
    this._applyCardSettings(out);

    return out.innerHTML;
  }

  /**
   * Fetch the unified variant fragment for a single sibling product.
   *
   * Returns a DETACHED CLONE of the #arn-variant-fragment root, so the
   * parser Document can be garbage-collected immediately. The clone has
   * a fresh ownerDocument (the current document) which makes subsequent
   * cloneNode operations in _distributeFragment slightly cheaper too.
   */
  async _fetchVariantFragment(handle) {
    let url = `${Shopify.routes.root}collections/${this._col}/products/${handle}?section_id=arn-plp-slide-renderer`;
    url += this._cardSettingsParams();

    const html = await this._fetchSection(url, `variant:${handle}`);
    if (!html) return null;

    const parserDoc = new DOMParser().parseFromString(html, 'text/html');
    const original = parserDoc.getElementById('arn-variant-fragment');

    if (!original) {
      ARN_ERR(`Variant fragment for "${handle}" — #arn-variant-fragment not found. Preview:`, html.slice(0, 500));
      return null;
    }

    // Strip disabled elements on the parser document first, then adopt the
    // fragment into the main document so the parser Document is no longer
    // referenced and can be GCed.
    this._applyCardSettings(original);
    const fragment = document.importNode(original, /* deep */ true);

    return fragment;
  }

  // ── Card assembly ──────────────────────────────────────────────────────────

  /**
   * Fetch primary card + all sibling variant fragments and return a fully
   * assembled <li> ready to be inserted into the live DOM.
   *
   * ── The Swiper-pending problem ───────────────────────────────────────────
   * When the browser parses HTML containing a custom element (e.g.
   * arn-product-card-slider-component), it upgrades it synchronously and
   * fires the constructor() immediately — even if the element is inside a
   * detached <li> created via `document.createElement`.
   *
   * ArnProductCardSliderComponent.constructor() calls initializeSwiper(),
   * which runs `new window.Swiper(...)` while the element has no layout
   * dimensions (clientWidth = 0, offsetLeft = 0 on every slide). Swiper
   * then thinks there is only 1 visible slide and locks the navigation.
   *
   * The fix is a two-phase approach:
   *
   *   Phase 1 — build in a "cold" document (no upgrades):
   *     Use DOMParser to produce the full, assembled HTML string without ever
   *     touching the live document or creating real custom elements.
   *
   *   Phase 2 — stamp data-swiper-pending before connecting to the live DOM:
   *     Inject the assembled HTML into the detached <li> using innerHTML.
   *     The browser upgrades custom elements at this point and the constructor
   *     fires — but initializeSwiper() immediately returns when it sees
   *     data-swiper-pending="true", so Swiper never runs on a zero-dimension
   *     element.
   *     replaceSkeleton() then inserts the <li> into the live DOM, removes
   *     the attribute, and calls initializeSwiper() explicitly so Swiper can
   *     read real layout dimensions.
   */
  async fetchAndAssembleCard(
    primaryHandle,
    siblingHandles,
    disableGrouping,
    collectionMocoOrder = [],
    allowedHandles = null
  ) {
    // ── Phase 1: fetch primary card HTML ─────────────────────────────────────

    const primaryHtml = await this._fetchFullCard(primaryHandle, disableGrouping);
    if (!primaryHtml) return null;

    // ── Swiper-pending guard ─────────────────────────────────────────────────
    // Inject data-swiper-pending="true" into the raw HTML string BEFORE
    // assigning to innerHTML, so the constructor sees it at upgrade time.
    const pendingHtml = primaryHtml.replace(
      /(<arn-product-card-slider-component\b)/,
      '$1 data-swiper-pending="true"'
    );

    const li = document.createElement('li');
    li.innerHTML = pendingHtml;

    const sliderComp = li.querySelector('arn-product-card-slider-component');

    const swiperWrapper = li.querySelector('.swiper-wrapper');
    if (!swiperWrapper) {
      ARN_WARN(`fetchAndAssembleCard — ${primaryHandle}: .swiper-wrapper not found`);
      return li;
    }

    // Liquid renders siblings from the unfiltered collection page. When a
    // filter is active (or grouping is off), drop every color that is not
    // allowed before counting slides or injecting siblings.
    if (allowedHandles?.length) {
      this._retainAllowedVariants(li, allowedHandles, primaryHandle);
    }

    // ── Ungrouped / single-slide fast paths ──────────────────────────────────

    // Defensive strip: when grouping is disabled, ensure the card has exactly
    // one slide even if Liquid rendered extras (e.g. served from cache without
    // the disable_grouping=1 param). Keep the requested product, not whichever
    // color the collection order put first.
    if (disableGrouping) {
      this._enforceSingleSlide(li, primaryHandle);
      return li;
    }

    const liquidRendered = swiperWrapper.querySelectorAll(':scope > .swiper-slide').length;

    if (!siblingHandles.length && liquidRendered <= 1) {
      ARN_LOG(`fetchAndAssembleCard — ${primaryHandle}: no siblings needed`);
      this._enforceSingleSlide(li, primaryHandle);
      return li;
    }

    // ── Sibling injection path ───────────────────────────────────────────────

    if (liquidRendered >= siblingHandles.length + 1) {
      ARN_LOG(`fetchAndAssembleCard — ${primaryHandle}: Liquid already rendered ${liquidRendered} slides, skipping JS injection`);
    } else {
      // Determine which siblings Liquid did NOT render.
      const renderedHandles = new Set([normalizeMocoHandle(primaryHandle)]);
      swiperWrapper.querySelectorAll(':scope > .swiper-slide').forEach(slide => {
        const h = normalizeMocoHandle(slide.dataset.plpProductHandle)
          || normalizeMocoHandle(_handleFromUrl(slide.querySelector('a[href]')?.getAttribute('href')));
        if (h) renderedHandles.add(h);
      });

      const missingSiblings = siblingHandles.filter(
        h => !renderedHandles.has(normalizeMocoHandle(h))
      );

      if (!missingSiblings.length) {
        ARN_LOG(`fetchAndAssembleCard — ${primaryHandle}: all siblings already in card`);
      } else {
        ARN_LOG(`fetchAndAssembleCard — ${primaryHandle}: injecting ${missingSiblings.length} missing sibling(s): [${missingSiblings.join(', ')}]`);

        // Resolve destination containers from VARIANT_ZONE_TARGETS up-front.
        // Wishlist and other optional zones may be absent when disabled in
        // arnPlpConfig.card — this is expected and not a warning condition.
        const zoneTargets = {};
        for (const [zone, selector] of Object.entries(VARIANT_ZONE_TARGETS)) {
          zoneTargets[zone] = li.querySelector(selector);
        }

        // Fetch all missing sibling fragments concurrently, then process
        // results in VariantHandles order. The collection order is applied to
        // every completed zone in one deterministic pass below.
        const fetchedFragments = await Promise.all(
          missingSiblings.map(h => this._fetchVariantFragment(h))
        );

        let slideCount = liquidRendered;
        for (const [i, fragment] of fetchedFragments.entries()) {
          const h = missingSiblings[i];
          if (!fragment) continue;

          slideCount++;
          this._distributeFragment(fragment, zoneTargets, slideCount);
          ARN_LOG(`fetchAndAssembleCard — "${h}": injected variant fragment at provisional index ${slideCount}`);
        }

        // Drop references to fetched fragments so the parser-imported nodes can
        // be garbage-collected once their cloned descendants are detached.
        fetchedFragments.length = 0;
      }
    }

    // ── Apply ST collection order ────────────────────────────────────────────
    // The manual collection scan is the PLP source of truth. Availability and
    // VariantHandles order are used only as a fallback when no collection rank
    // could be resolved.
    const collectionOrdered = this._applyCollectionMocoOrder(
      li,
      collectionMocoOrder,
      primaryHandle
    );
    if (!collectionOrdered) {
      this._sortUnavailableLast(swiperWrapper, primaryHandle);
    }

    // ── Assign final data-variant-index uniformly across all zones ───────────
    this._assignFinalIndices(li);

    // ── Final UI tweaks (counters, labels, nav buttons) ──────────────────────
    this._finaliseUi(li, swiperWrapper, sliderComp);

    return li;
  }

  /**
   * Distribute the data-zone pieces of a fetched variant fragment into the
   * matching containers of the primary card. Slide images get an id (used
   * by the legacy counter logic in ArnProductCardSliderComponent); the
   * debug badge is appended to the header-info zone when enabled. Final
   * data-variant-index assignment happens later in _assignFinalIndices.
   */
  _distributeFragment(fragment, zoneTargets, slideCount) {
    for (const piece of fragment.querySelectorAll('[data-zone]')) {
      const zoneName = piece.dataset.zone;
      const target = zoneTargets[zoneName];
      if (!target) continue; // zone disabled in card settings or unknown

      const clone = piece.cloneNode(true);

      // Slide images additionally need an id="Slide-N" for the legacy
      // counter logic in ArnProductCardSliderComponent. All other zones
      // identify themselves via data-variant-index alone.
      if (zoneName === 'slide-image') {
        clone.id = `Slide-${slideCount}`;
      }

      // Debug badge — appended into the header-info zone of injected variants
      // to visually distinguish JS-injected from Liquid-rendered content.
      if (zoneName === 'header-info') {
        const debugBadge = this._buildDebugBadge();
        if (debugBadge) clone.appendChild(debugBadge);
      }

      target.appendChild(clone);
    }
  }

  /**
   * Reorder every variant-aware card zone by the manual collection sequence
   * written by ST. All moves happen while data-swiper-pending is still set, so
   * Swiper and data-active-variant see the final order on first initialization.
   *
   * Returns false without moving any node if the card markup is incomplete or
   * none of its handles is ranked in the collection. The caller can then retain
   * the previous availability/VariantHandles fallback.
   */
  _applyCollectionMocoOrder(li, collectionMocoOrder, primaryHandle) {
    if (!collectionMocoOrder?.length) return false;

    const swiperWrapper = li.querySelector('.swiper-wrapper');
    if (!swiperWrapper) return false;

    const slides = Array.from(swiperWrapper.children).filter(
      child => child.classList.contains('swiper-slide')
    );
    if (slides.length < 2) return false;

    const renderedHandles = slides.map(
      slide => normalizeMocoHandle(slide.dataset.plpProductHandle)
    );
    if (renderedHandles.some(handle => !handle)) {
      ARN_WARN(`_applyCollectionMocoOrder — ${primaryHandle}: slide handle marker missing`);
      return false;
    }

    const collectionHandleSet = new Set(
      collectionMocoOrder.map(normalizeMocoHandle)
    );
    if (!renderedHandles.some(handle => collectionHandleSet.has(handle))) {
      return false;
    }

    // Sold-out colors must never open the card, whatever their collection rank.
    const unavailableHandles = new Set(
      slides
        .filter(slide => slide.dataset.unavailable === 'true')
        .map(slide => normalizeMocoHandle(slide.dataset.plpProductHandle))
    );

    const orderedHandles = orderMocoHandlesByCollection(
      renderedHandles,
      collectionMocoOrder,
      unavailableHandles
    );
    const wrapperSelectors = ['.swiper-wrapper', ...VARIANT_INDEX_ZONE_SELECTORS];
    const reorderPlans = [];

    // Preflight every present wrapper before moving anything. Each zone must
    // contain the same handle set as the slides or indices would desynchronise.
    for (const selector of wrapperSelectors) {
      const wrapper = li.querySelector(selector);
      if (!wrapper) continue;

      const children = Array.from(wrapper.children);
      if (children.length !== orderedHandles.length) {
        ARN_WARN(`_applyCollectionMocoOrder — ${primaryHandle}: ${selector} has ${children.length}/${orderedHandles.length} variant nodes`);
        return false;
      }

      const childrenByHandle = new Map();
      for (const child of children) {
        const handle = normalizeMocoHandle(child.dataset.plpProductHandle);
        if (!handle) {
          ARN_WARN(`_applyCollectionMocoOrder — ${primaryHandle}: ${selector} handle marker missing`);
          return false;
        }
        const bucket = childrenByHandle.get(handle) || [];
        bucket.push(child);
        childrenByHandle.set(handle, bucket);
      }

      const orderedChildren = orderedHandles
        .map(handle => childrenByHandle.get(handle)?.shift())
        .filter(Boolean);
      if (
        orderedChildren.length !== children.length
        || [...childrenByHandle.values()].some(bucket => bucket.length)
      ) {
        ARN_WARN(`_applyCollectionMocoOrder — ${primaryHandle}: ${selector} handle set differs from slides`);
        return false;
      }

      reorderPlans.push({ wrapper, orderedChildren });
    }

    for (const { wrapper, orderedChildren } of reorderPlans) {
      orderedChildren.forEach(child => wrapper.appendChild(child));
    }

    const orderedSlides = Array.from(
      swiperWrapper.querySelectorAll('.swiper-slide')
    );
    orderedSlides.forEach((slide, index) => {
      slide.id = `Slide-${index + 1}`;
    });

    const card = li.querySelector('.arn-product-card');
    if (card) card.dataset.mocoOrderSource = 'collection';

    ARN_LOG(`_applyCollectionMocoOrder — ${primaryHandle}: [${orderedHandles.join(', ')}]`);
    return true;
  }

  /**
   * Fallback: reorder slides so that data-unavailable="true" ones end up last
   * when no ST collection rank could be resolved.
   * DOM-move is stable (no clone) so all slide state (event listeners,
   * child references) is preserved.
   *
   * Contract: this method ONLY touches the swiper-wrapper. It updates the
   * slide `id` attributes to reflect their new positions but does NOT
   * touch data-variant-index — that's exclusively the job of
   * _assignFinalIndices which runs immediately after and assigns indices
   * uniformly across all zone wrappers in one pass.
   */
  _sortUnavailableLast(swiperWrapper, primaryHandle) {
    const allSlides = Array.from(swiperWrapper.querySelectorAll('.swiper-slide'));

    const sorted = [
      ...allSlides.filter(s => s.dataset.unavailable !== 'true'),
      ...allSlides.filter(s => s.dataset.unavailable === 'true'),
    ];

    const orderChanged = sorted.some((s, i) => s !== allSlides[i]);
    if (!orderChanged) return;

    ARN_LOG(`_sortUnavailableLast — ${primaryHandle}: reordering slides to put unavailable last`);
    sorted.forEach(slide => swiperWrapper.appendChild(slide));
    sorted.forEach((slide, i) => { slide.id = `Slide-${i + 1}`; });
  }

  /**
   * Walk each zone wrapper in DOM order (after the unavailability sort) and
   * assign a fresh 1-based data-variant-index. Because at this point each
   * wrapper contains exactly one element per variant in the same order as
   * the slides, a simple positional walk is correct — no class-based
   * filtering needed.
   *
   * This single uniform pass replaces the previous version's 4 separate
   * reindex loops plus the colour span rebuild — all four index families
   * collapsed into one.
   */
  _assignFinalIndices(li) {
    const swiperWrapper = li.querySelector('.swiper-wrapper');
    const slides = Array.from(swiperWrapper.querySelectorAll('.swiper-slide'));

    // Slides themselves
    slides.forEach((slide, i) => {
      slide.dataset.variantIndex = String(i + 1);
    });

    // Each zone wrapper holds N elements in 1:1 order with the N slides.
    // We index them by DOM position, accepting any element child — no
    // class allow-list, no fragile coupling to the zone snippet markup.
    const applyIndexInOrder = (wrapperSelector) => {
      const wrapper = li.querySelector(wrapperSelector);
      if (!wrapper) return;

      let i = 0;
      for (const el of wrapper.children) {
        if (el.nodeType !== Node.ELEMENT_NODE) continue;
        el.dataset.variantIndex = String(++i);
      }
    };

    for (const selector of VARIANT_INDEX_ZONE_SELECTORS) {
      applyIndexInOrder(selector);
    }

    ARN_LOG(`_assignFinalIndices — assigned final variant indices 1..${slides.length} uniformly across all zones`);
  }

  /**
   * Final UI fix-ups for multi-slide cards:
   *   - update aria-labels with the final slide count
   *   - update the mobile counter total
   *   - enable swiper navigation + reveal nav buttons on hover
   *   - update the desktop "N Colors" label
   *
   * For single-slide cards (no siblings) nothing here applies — the Liquid
   * already rendered the correct single-variant markup.
   */
  _finaliseUi(li, swiperWrapper, sliderComp) {
    const totalSlides = swiperWrapper.querySelectorAll('.swiper-slide').length;

    swiperWrapper.querySelectorAll('.swiper-slide').forEach((s, i) => {
      s.setAttribute('aria-label', `${i + 1} / ${totalSlides}`);
    });

    const totalEl = li.querySelector('.slider-counter--total');
    if (totalEl) totalEl.textContent = String(totalSlides);

    if (totalSlides <= 1) {
      ARN_LOG(`_finaliseUi — single-slide card, no UI tweaks needed`);
      return;
    }

    // Enable swiper navigation
    if (sliderComp) sliderComp.dataset.scrollDisabled = 'false';

    // Nav buttons: add hover-reveal class missing when Liquid rendered output_variants.size == 1
    const prevBtn = li.querySelector('button[name="previous"]');
    const nextBtn = li.querySelector('button[name="next"]');
    [prevBtn, nextBtn].forEach(btn => {
      if (btn) btn.classList.add('sd:group-hover/product-card:tw-block');
    });

    // Desktop "N Colors" label
    const colorsLabel = li.querySelector('[data-colors-label]');
    if (colorsLabel) {
      colorsLabel.classList.add('sd:group-hover/product-card:tw-hidden');
      colorsLabel.textContent = `${totalSlides} Colors`;
    }

    // Ensure color display wrapper shows on hover (missing class on single-variant renders)
    const colorWrapper = li.querySelector('[data-color-display-wrapper]');
    if (colorWrapper) {
      colorWrapper.classList.add('sd:group-hover/product-card:tw-block');
    }

    ARN_LOG(`_finaliseUi — final slide count: ${totalSlides}`);
  }

  /**
   * Remove colorways whose handle is not in the allowed set.
   *
   * The section renderer has no access to the PLP filter query, so Liquid
   * can paint siblings from the unfiltered collection. Those slides must not
   * survive when the shopper filtered by lens color, material, polarized, etc.
   *
   * @param {HTMLElement} li
   * @param {string[]} allowedHandles
   * @param {string} primaryHandle
   * @returns {number} Nodes removed.
   */
  _retainAllowedVariants(li, allowedHandles, primaryHandle) {
    const allowed = new Set(
      [...(allowedHandles || []), primaryHandle]
        .map((handle) => normalizeMocoHandle(handle))
        .filter(Boolean)
    );
    if (!allowed.size) return 0;

    let removed = 0;
    li.querySelectorAll('[data-plp-product-handle]').forEach((el) => {
      const handle = normalizeMocoHandle(el.dataset.plpProductHandle);
      if (handle && !allowed.has(handle)) {
        el.remove();
        removed += 1;
      }
    });
    if (removed) {
      ARN_LOG(`_retainAllowedVariants — ${primaryHandle}: removed ${removed} off-filter node(s)`);
    }
    return removed;
  }

  /**
   * Collapse a card to the requested product.
   *
   * When Liquid was served a stale cache without disable_grouping=1 it renders
   * every sibling and then reorders them by collection rank. Keeping slide 1
   * would show that first color — and its price — on every card of the model.
   * The requested handle stays; every other colorway is removed. The remaining
   * zone is reindexed to 1 so the active-variant CSS still reveals it.
   *
   * @param {HTMLElement} li
   * @param {string} primaryHandle
   */
  _enforceSingleSlide(li, primaryHandle) {
    const primary = normalizeMocoHandle(primaryHandle);
    const sw = li.querySelector('.swiper-wrapper');
    if (!sw) return;

    const allSlides = Array.from(sw.querySelectorAll(':scope > .swiper-slide'));
    if (allSlides.length > 1) {
      const keep = allSlides.find(
        (slide) => normalizeMocoHandle(slide.dataset.plpProductHandle) === primary
      ) || allSlides[0];
      const keepHandle = normalizeMocoHandle(keep.dataset.plpProductHandle);

      ARN_WARN(`_enforceSingleSlide — ${primaryHandle}: disableGrouping=true but Liquid rendered ${allSlides.length} slides — keeping "${keepHandle}"`);
      allSlides.forEach((slide) => {
        if (slide !== keep) slide.remove();
      });
      li.querySelectorAll('[data-plp-product-handle]').forEach((el) => {
        const handle = normalizeMocoHandle(el.dataset.plpProductHandle);
        if (handle && handle !== keepHandle) el.remove();
      });
    }

    li.querySelectorAll('[data-variant-index]').forEach((el) => {
      el.dataset.variantIndex = '1';
    });
    const cardRoot = li.querySelector('.arn-product-card');
    if (cardRoot) cardRoot.dataset.activeVariant = '1';

    // Reset slider to single-slide state
    const sc = li.querySelector('arn-product-card-slider-component');
    if (sc) sc.dataset.scrollDisabled = 'true';

    // Show single color name in label (no hover needed)
    const colorsLabel = li.querySelector('[data-colors-label]');
    const colorSpan = li.querySelector('.current-color-display');
    if (colorsLabel) {
      colorsLabel.textContent = colorSpan ? colorSpan.textContent.trim() : '';
      colorsLabel.classList.remove('sd:group-hover/product-card:tw-hidden');
    }

    // On a single-slide card the color display wrapper must never show on hover.
    const colorWrapper = li.querySelector('[data-color-display-wrapper]');
    if (colorWrapper) {
      colorWrapper.classList.remove('sd:group-hover/product-card:tw-block');
      colorWrapper.classList.add('tw-hidden');
    }

    // Update mobile counter
    const totalEl = li.querySelector('.slider-counter--total');
    if (totalEl) totalEl.textContent = '1';
  }

  // ── Live DOM commit ────────────────────────────────────────────────────────

  /**
   * Replace a skeleton placeholder with the fully assembled card <li>.
   *
   * This is the only place a card enters the live DOM. After insertion we
   * remove data-swiper-pending and explicitly call initializeSwiper() so
   * Swiper initialises against an element that has real layout dimensions.
   *
   * Two edge-case scenarios are also handled defensively:
   *
   *   A) Swiper managed to initialise despite the pending guard (e.g. a
   *      setTimeout retry fired after insertion but before we called
   *      initializeSwiper). We detect the broken instance via
   *      sliderComp.swiper, destroy it, and re-initialise cleanly.
   *
   *   B) initializeSwiper is not yet a function (ArnProductCardSliderComponent
   *      not loaded). We warn and skip — the component will self-initialise
   *      once its script is evaluated.
   */
  replaceSkeleton(skeletonEl, cardLi) {
    if (!cardLi) {
      ARN_WARN('replaceSkeleton — null card, removing skeleton');
      skeletonEl.remove();
      return;
    }

    skeletonEl.replaceWith(cardLi);

    const sliderComp = cardLi.querySelector('arn-product-card-slider-component');
    if (!sliderComp) return;

    const totalSlides = sliderComp.querySelectorAll('.swiper-wrapper .swiper-slide').length;
    ARN_LOG(`replaceSkeleton — card inserted, slider has ${totalSlides} slide(s), pending: ${sliderComp.dataset.swiperPending}`);

    delete sliderComp.dataset.swiperPending;

    if (sliderComp.swiper) {
      ARN_LOG(`replaceSkeleton — destroying pre-existing Swiper instance for re-init`);
      sliderComp.swiper.destroy(true, true);
      sliderComp.swiper = null;
    }

    if (typeof sliderComp.initializeSwiper === 'function') {
      ARN_LOG(`replaceSkeleton — calling initializeSwiper() after DOM insertion`);
      sliderComp.initializeSwiper();
    } else {
      ARN_WARN(`replaceSkeleton — initializeSwiper() not found on slider component; Swiper will self-initialise when the component script loads`);
    }
  }
}

// #endregion

// #region Media Block Manager
// ════════════════════════════════════════════════════════════════════════════

/**
 * Manages promotional media blocks (image/video tiles) interleaved between
 * product cards at configured positions.
 *
 * Templates are pre-rendered in the DOM by Liquid as <template
 * data-media-block-id>; this class clones them on demand and inserts them
 * at the right grid position once enough product cards are present.
 *
 * Position semantics are "after N products" (1-based, from section settings):
 * a position of 4 means the block appears after the 4th product card.
 */
class ArnPlpMediaBlockManager {
  constructor(grid, enabled) {
    this._grid = grid;
    this._enabled = enabled;
    this._templates = new Map();
    this._inserted = new Set();
    this._blocks = [];
    if (enabled) this._collectTemplates();
  }

  _collectTemplates() {
    const templates = document.querySelectorAll('template[data-media-block-id]');
    ARN_LOG(`Media blocks: found ${templates.length} template(s) in DOM`);
    templates.forEach(t => this._templates.set(t.dataset.mediaBlockId, t));
  }

  setBlocks(blocks) {
    this._blocks = [...blocks].sort((a, b) => a.position - b.position);
    if (this._blocks.length) ARN_LOG(`Media blocks configured: ${this._blocks.map(b => `"${b.id}" after position ${b.position}`).join(', ')}`);
  }

  /**
   * Insert any media blocks whose position threshold has been reached.
   * Must be called AFTER a card has been committed to the live grid so that
   * the reference card node is stable and already in the DOM.
   *
   * We wait until cardCount >= position + 1 before inserting, ensuring the
   * card that will follow the media block is already in the DOM. This makes
   * insertBefore stable and avoids the block being appended at the end when
   * the reference card hasn't been committed yet.
   */
  insertDue(cardCount) {
    if (!this._enabled) return;
    for (const block of this._blocks) {
      if (this._inserted.has(block.id)) continue;

      // position=4 means "after 4 cards", so we need at least 5 cards before
      // we can insertBefore the 5th (cards[4]). If position === 0, insert
      // immediately (before all cards), so threshold is 1 in that edge case.
      const threshold = block.position === 0 ? 1 : block.position + 1;
      if (cardCount < threshold) continue;

      const tmpl = this._templates.get(block.id);
      if (!tmpl) { ARN_WARN(`Media block "${block.id}" — no <template> found in DOM, skipping`); continue; }

      const cloned = tmpl.content.cloneNode(true);
      const mediaLi = cloned.firstElementChild;
      if (!mediaLi || mediaLi.tagName !== 'LI') {
        ARN_WARN(`MediaBlockManager — template for "${block.id}" does not start with <li>, skipping`);
        continue;
      }
      mediaLi.setAttribute('data-media-block', block.id);

      // Query product cards only (exclude skeletons and already-inserted media blocks).
      const cards = Array.from(this._grid.querySelectorAll('li:not([data-skeleton]):not([data-media-block])'));
      const refNode = cards[block.position] ?? null;

      if (refNode) {
        this._grid.insertBefore(mediaLi, refNode);
      } else {
        // Fallback: all cards are already rendered and position exceeds the
        // total count — append at end.
        this._grid.appendChild(mediaLi);
      }

      this._inserted.add(block.id);
      ARN_LOG(`MediaBlockManager — inserted block "${block.id}" at position ${block.position} (cardCount: ${cardCount}, refNode: ${refNode ? 'found' : 'appended at end'})`);
    }
  }

  static shouldShow() { return !ArnPlpUrlParser.hasActiveFilters(); }
}

// #endregion

// #region Discount Kit Integration
// ════════════════════════════════════════════════════════════════════════════

/**
 * Discount Kit integration helper.
 *
 * Discount Kit prices are resolved server-side in component.product-price /
 * arn-plp-product-card-zone, and patched client-side by arn-dk-storefront-prices.js
 * (for cookie/code-based discounts). That engine scans the DOM once at page load —
 * at that point the grid is empty because ArnLoadMore hasn't rendered any cards yet.
 * After each render batch we explicitly ask it to re-run pricing on the newly
 * inserted cards via window.ArnDkStorefrontPrices.refreshWithin().
 */
class ArnPlpDiscountKit {
  /**
   * Notify Discount Kit that new collection products have been added so it can
   * apply automatic / code-based sale pricing to the cards just committed to
   * the DOM. Safe to call on every render chunk.
   */
  notify(root = document) {
    const apply = () => {
      try {
        if (window.ArnDkStorefrontPrices?.refreshWithin) {
          window.ArnDkStorefrontPrices.syncAppliedCodesFromCookies?.();
          window.ArnDkStorefrontPrices.refreshWithin(root);
          ARN_LOG('DiscountKit — refreshed storefront prices on new cards');
          return true;
        }
      } catch (err) {
        ARN_WARN('DiscountKit — refresh threw:', err);
        return true;
      }
      return false;
    };

    if (apply()) return;

    // Engine is a deferred bundle; if it has not booted yet, retry briefly so the
    // freshly injected cards still get DK-patched.
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      if (apply() || tries >= 10) clearInterval(timer);
    }, 300);
  }
}

// #endregion

// #region Tealium Tracking
// ════════════════════════════════════════════════════════════════════════════

/**
 * Builds and maintains the Tealium tracking data layer for the PLP, mirroring
 * the server-side output of `snippets/tealium-products.liquid` in the
 * `when 'collection'` branch.
 *
 * ── Why this is needed ──────────────────────────────────────────────────────
 * In Liquid-native mode, `tealium-products.liquid` iterates `collection.products`
 * at render time and bakes the full products + mappingVariantIdsUpcs object
 * into the page. In Storefront API mode the grid is rendered empty at first
 * paint and populated by JS — so the server-rendered data layer alone would be
 * incomplete and misaligned with the actual visible cards.
 *
 * This tracker ADDS to the existing data layer as the engine fetches products,
 * on both the initial load and every Load More batch. The Liquid-rendered
 * server-side entries are preserved as a useful first-paint fallback for the
 * VirtualPage-View that tealium.js fires at DOMContentLoaded, before the
 * first batch lands. The tracker then publishes plpProductsLoaded at the end
 * of the fill cycle so tealium.js can dispatch an updated VirtualPage-View
 * with the now-complete data layer.
 *
 * ── What gets tracked ───────────────────────────────────────────────────────
 * The shape mirrors the Liquid output exactly so downstream Tealium tags
 * receive identical data regardless of the engine mode:
 *
 *   products: {
 *     "<UPC>": {
 *       Upc: "<UPC>",
 *       Status: "Available" | "Coming soon" | "Out-of-stock",
 *       LensType: "PLANO",
 *       Price: "175.00",
 *       PriceFull: "175.00",       // compare_at_price when on sale, else price
 *       Category: "OPTICS",
 *       OosOptions: "SearchSimilar" | "EmailMeWhenAvailable" // omitted when Available
 *     },
 *     ...
 *   }
 *
 *   mappingVariantIdsUpcs: {
 *     "<variant_id>": "<UPC>",
 *     ...
 *   }
 *
 * ── Sibling inclusion ───────────────────────────────────────────────────────
 * Every product visible on the PLP gets tracked — both the primary product
 * of a card and any sibling resolved from the variantshandles metafield.
 * This is critical because the wishlist (and other Tealium-tracked actions)
 * may target a sibling variant directly, and without it the variantId →
 * UPC lookup would fail at click time.
 *
 * ── Duplicate UPC handling ──────────────────────────────────────────────────
 * Mirrors the Liquid behaviour: when the same UPC appears on multiple
 * products it is suffixed with `#N` (e.g. "0888392528476#2") so the products
 * object remains a unique-keyed map. The mappingVariantIdsUpcs always points
 * to the canonical (un-suffixed) UPC.
 */
class ArnPlpTealiumTracker {
  constructor() {
    // Ensure the global namespace exists. We deliberately DO NOT wipe
    // products/mappingVariantIdsUpcs here: those are populated server-side
    // by tealium-products.liquid (Liquid-native mode emits the correct data;
    // in Storefront engine mode the data is imprecise but it acts as a
    // useful first-paint fallback for the initial VirtualPage-View dispatch
    // that tealium.js fires before our first batch lands).
    //
    // The engine then ADDS to / UPDATES those entries via ingestBatch() as
    // products are fetched: matching UPCs are overwritten with correct data,
    // missing siblings are appended, and the variantId → UPC mapping is
    // completed. After the first fill cycle a second VirtualPage-View is
    // dispatched (see ArnLoadMore._fillToTarget) so Tealium sees the
    // up-to-date data layer.
    window.tealiumTrackingUtils = window.tealiumTrackingUtils || {};
    window.tealiumTrackingUtils.products = window.tealiumTrackingUtils.products || {};
    window.tealiumTrackingUtils.mappingVariantIdsUpcs = window.tealiumTrackingUtils.mappingVariantIdsUpcs || {};

    // searchResultItemsQnt IS reset: Liquid emits collection.products_count
    // which is the raw (pre-grouping) total, misleading for the Storefront
    // engine view of the grid. It will be re-set to the correct visible card
    // count by setCardCount() at the end of each fill cycle.
    window.tealiumTrackingUtils.searchResultItemsQnt = '0';

    // UPC occurrence counter for deduplication suffix (mirrors Liquid's
    // upc_list / upc_count logic). Keys are canonical UPCs, values are
    // the number of products already registered with that UPC.
    this._upcCounts = new Map();

    // Track product handles already ingested so re-ingestion across batches
    // (a sibling resolved in batch N may be a primary in batch N+1, or vice
    // versa) doesn't inflate the duplicate-UPC counter.
    this._ingestedHandles = new Set();

    // Track the number of distinct cards rendered (post-grouping) so the
    // searchResultItemsQnt reflects the visible grid, not the raw collection.
    this._cardCount = 0;

    ARN_LOG('TealiumTracker — initialised; products/mapping preserved, searchResultItemsQnt reset to 0');
  }

  /**
   * Format a Storefront API money amount (decimal string like "175.0") into
   * a two-decimal fixed-point string ("175.00"). Returns empty string on
   * missing/invalid input.
   */
  _formatPrice(moneyAmount) {
    if (moneyAmount == null) return '';
    const n = parseFloat(moneyAmount);
    if (Number.isNaN(n)) return '';
    return n.toFixed(2);
  }

  /**
   * Select the "selected_or_first_available_variant" equivalent from a
   * Storefront API product node. Mirrors Liquid's behaviour:
   *   1. If any variant is availableForSale, return the first such one.
   *   2. Otherwise fall back to the first variant in the list.
   *   3. If no variants at all, return null.
   */
  _pickRepresentativeVariant(productNode) {
    const edges = productNode?.variants?.edges;
    if (!edges || !edges.length) return null;
    const variants = edges.map(e => e.node);
    const firstAvailable = variants.find(v => v.availableForSale);
    return firstAvailable || variants[0] || null;
  }

  // Category vocabulary shared with the Liquid producers and checkout pixel.
  _normalizeCategory(...values) {
    const categories = {
      sun: 'SUN', sunglasses: 'SUN', sunglass: 'SUN',
      'occhiali da sole': 'SUN', 'lunettes de soleil': 'SUN', sonnenbrille: 'SUN', sonnenbrillen: 'SUN', 'gafas de sol': 'SUN',
      eye: 'OPTICS', optics: 'OPTICS', optical: 'OPTICS', eyeglasses: 'OPTICS', eyeglass: 'OPTICS',
      'occhiali da vista': 'OPTICS', 'lunettes de vue': 'OPTICS', brille: 'OPTICS', brillen: 'OPTICS',
      korrektionsbrille: 'OPTICS', korrektionsbrillen: 'OPTICS', 'gafas graduadas': 'OPTICS',
      accessories: 'ACCESSORIES', accessory: 'ACCESSORIES', afa: 'AFA',
      electronics: 'Electronics', electronic: 'Electronics',
      giftcard: 'GIFTCARD', 'gift card': 'GIFTCARD', 'gift cards': 'GIFTCARD',
      'contact lens': 'CONTACT LENSES', 'contact lenses': 'CONTACT LENSES',
      vas: 'VAS', service: 'VAS', services: 'VAS', 'value added service': 'VAS', 'value added services': 'VAS'
    };
    for (const value of values) {
      const category = String(value || '').trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
      if (Object.prototype.hasOwnProperty.call(categories, category)) return categories[category];
    }
    return 'EYEWEAR';
  }

  _computeStatus(productNode, variant) {
    // The selected variant is authoritative, including an explicit false value.
    const disco = String(variant?.discoMetafield?.value ?? '').toLowerCase();
    if (variant?.availableForSale) return 'Available';
    return disco === 'true' ? 'Out-of-stock' : 'Coming soon';
  }

  /**
   * Apply the duplicate-UPC suffix rule, mirroring the Liquid logic.
   * The first occurrence of an UPC gets the bare UPC as its index; every
   * subsequent occurrence is suffixed "#2", "#3", etc.
   */
  _resolveProductIndex(upc) {
    const seen = this._upcCounts.get(upc) || 0;
    this._upcCounts.set(upc, seen + 1);
    return seen === 0 ? upc : `${upc}#${seen + 1}`;
  }

  /**
   * Ingest a single Storefront API product node into the data layer.
   * Skips products already ingested (tracked by handle in _ingestedHandles)
   * to keep the duplicate-UPC counter accurate across batches.
   *
   * Field set mirrors snippets/tealium-products.liquid exactly:
   *   Upc, Category, Status, OosOptions, LensType, Price, PriceFull.
   * All money fields are formatted as "2 decimals, no currency".
   */
  _ingestProduct(productNode) {
    if (!productNode?.handle) return;
    if (this._ingestedHandles.has(productNode.handle)) return;
    this._ingestedHandles.add(productNode.handle);

    const variant = this._pickRepresentativeVariant(productNode);
    if (!variant) {
      ARN_WARN(`TealiumTracker — product "${productNode.handle}" has no variants, skipping`);
      return;
    }

    const candidates = [variant.barcode, variant.sku].map(value => String(value || '').trim()).filter(Boolean);
    const upc = candidates.find(value => /^(?:\d{8}|\d{12,14})$/.test(value)) || '';
    if (!upc) return;
    const status = this._computeStatus(productNode, variant);
    const price = this._formatPrice(variant.price?.amount);
    // PriceFull mirrors Liquid: use compare_at_price when it's > price,
    // otherwise fall back to the regular price.
    const compare = parseFloat(variant.compareAtPrice?.amount);
    const regular = parseFloat(variant.price?.amount);
    const onSale = !Number.isNaN(compare) && !Number.isNaN(regular) && compare > regular;
    const priceFull = onSale
      ? this._formatPrice(variant.compareAtPrice.amount)
      : price;

    const productIndex = this._resolveProductIndex(upc);

    window.tealiumTrackingUtils.products[productIndex] = {
      Upc: upc,
      Category: this._normalizeCategory(productNode.categoryMetafield?.value, productNode.productType),
      Status: status,
      ...(status === 'Out-of-stock' ? { OosOptions: 'SearchSimilar' } :
        status === 'Coming soon' ? { OosOptions: 'EmailMeWhenAvailable' } : {}),
      LensType: 'PLANO',
      Price: price,
      PriceFull: priceFull,
    };

    if (variant.id && upc) {
      // Strip the Storefront API "gid://shopify/ProductVariant/12345" prefix
      // down to the numeric id so it matches the format Liquid emits.
      const numericVariantId = String(variant.id).split('/').pop();
      window.tealiumTrackingUtils.mappingVariantIdsUpcs[numericVariantId] = upc;
    }
  }

  /**
   * Ingest a list of product nodes (typically: primaries + their resolved
   * siblings for a render batch). De-duplication on `handle` is enforced
   * inside _ingestProduct so callers can safely pass overlapping lists.
   */
  ingestBatch(productNodes) {
    if (!productNodes?.length) return;

    let ingested = 0;
    for (const node of productNodes) {
      const before = this._ingestedHandles.size;
      this._ingestProduct(node);
      if (this._ingestedHandles.size > before) ingested++;
    }

    ARN_LOG(`TealiumTracker — ingested ${ingested} new product(s) into data layer (total: ${this._ingestedHandles.size})`);
  }

  /**
   * Update the visible-card counter, mirroring Liquid's
   * `searchResultItemsQnt: '{{ collection.products_count }}'`.
   * Called by the engine after each render batch with the current total
   * number of cards committed to the grid.
   *
   * NB: publishProductsLoaded is called by ArnLoadMore at the end of the
   * fill cycle, not here. A single fill cycle may invoke setCardCount
   * multiple times (when more than one Storefront API batch is needed to
   * reach TARGET_CARDS_PER_LOAD), so publishing here would over-fire the
   * Tealium VirtualPage-View. See ArnLoadMore._fillToTarget.
   */
  setCardCount(count) {
    this._cardCount = count;
    window.tealiumTrackingUtils.searchResultItemsQnt = String(count);
    ARN_LOG(`TealiumTracker — searchResultItemsQnt updated to ${count}`);
    ARN_LOG('TealiumTracker — data layer after batch:', {
      products: { ...window.tealiumTrackingUtils.products },
      mappingVariantIdsUpcs: { ...window.tealiumTrackingUtils.mappingVariantIdsUpcs },
      searchResultItemsQnt: window.tealiumTrackingUtils.searchResultItemsQnt,
    });
  }

  /**
   * Publish the PLP products loaded event using the theme's pub-sub system.
   *
   * tealium.js subscribes to this event (only when the Storefront engine mode
   * is active) and dispatches a Tealium VirtualPage-View with the current
   * Products data layer. In Liquid-native mode no subscriber is registered
   * and this publish is a silent no-op.
   *
   * Called once per fill cycle by ArnLoadMore — both the initial load and
   * each Load More click. A single fill cycle may internally span multiple
   * Storefront API batches (when more cards are needed to reach
   * TARGET_CARDS_PER_LOAD), but from Tealium's point of view it is one
   * logical page view update.
   *
   * Wrapped in a try/catch + presence guard so any environment issue
   * (e.g. the constants file not yet loaded) does not bubble up into the
   * engine's load cycle.
   */
  publishProductsLoaded() {
    try {
      if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
        ARN_WARN('TealiumTracker — publish() or PUB_SUB_EVENTS not available; skipping plpProductsLoaded');
        return;
      }
      publish(PUB_SUB_EVENTS.plpProductsLoaded, {});
      ARN_LOG('TealiumTracker — published plpProductsLoaded event');
    } catch (err) {
      ARN_WARN('TealiumTracker — publish threw:', err);
    }
  }
}

// #endregion

// #region Main Component
// ════════════════════════════════════════════════════════════════════════════

/**
 * The web component that orchestrates the entire PLP rendering pipeline.
 *
 * Lifecycle:
 *   - connectedCallback: build collaborators, pre-fill skeletons, kick off
 *     the initial fill cycle.
 *   - On Load More click: pre-fill skeletons again, start another fill cycle.
 *   - _fillToTarget: loop _loadBatch until TARGET_CARDS_PER_LOAD cards have
 *     been rendered, or the collection is exhausted; at the end, publish
 *     the Tealium plpProductsLoaded event so the data layer dispatch fires
 *     exactly once per logical user action.
 *   - _loadBatch: one Storefront API page → deduplicate → resolve siblings
 *     → ingest into Tealium data layer → render cards.
 *   - _renderCards: chunk groups by RENDER_CONCURRENCY, fetch+assemble in
 *     parallel, commit cards to live DOM, dispatch DN notification per chunk,
 *     update Tealium card count at the end.
 */
class ArnLoadMore extends HTMLElement {
  connectedCallback() {
    const cfg = window.arnPlpConfig || {};
    ARN_LOG('connectedCallback — config:', cfg);

    this._collectionHandle = cfg.collection?.handle || '';
    this._targetPerLoad = ARN_CFG.TARGET_CARDS_PER_LOAD;

    // Storefront client: arnPlpConfig.storefront (preferred) → window.storefront fallback.
    // The constructor validates url+token presence and throws on misconfiguration
    // so we fail loudly at boot rather than degrading into HTTP 404 loops later.
    try {
      this._storefront = new ArnPlpStorefrontClient(
        cfg.storefront || window.storefront || {}
      );
    } catch (err) {
      ARN_ERR('ArnLoadMore — Storefront client init failed:', err.message);
      // Disable Load More UI and bail; nothing useful can happen without the API.
      this.classList.add('tw-hidden');
      return;
    }

    this._grid = document.getElementById(this.dataset.gridId);
    this._counter = this.querySelector('.js-lm-counter');
    this._btn = this.querySelector('.js-lm-btn');
    this._spinner = this.querySelector('.arn-plp__loading-state');

    // Skeleton lifecycle is owned by the SkeletonManager. The renderer holds
    // a reference too so it can drive replaceSkeleton during the commit step.
    this._skeletonMgr = new ArnPlpSkeletonManager(this._grid);
    this._renderer = new ArnPlpCardRenderer(this._collectionHandle, this._skeletonMgr);

    this._mediaBlockMgr = new ArnPlpMediaBlockManager(
      this._grid,
      ArnPlpMediaBlockManager.shouldShow()
    );
    this._mediaBlockMgr.setBlocks(cfg.mediaBlocks || []);

    this._discountKit = new ArnPlpDiscountKit();

    // Tealium tracker preserves existing server-rendered data and adds to it
    // as products are fetched. See ArnPlpTealiumTracker for details.
    this._tealium = new ArnPlpTealiumTracker();

    // Counter is hidden at the start of every fill cycle and revealed only
    // once the batch completes with a real count (see _updateCounter).
    // This prevents showing a stale or partial number during loading.
    if (this._counter) this._counter.classList.add('tw-invisible');

    // State. Sort and filters are snapshotted once: Load More rewrites ?page=
    // and must not flip the grid back to manual order or drop the active filter.
    const sort = ArnPlpUrlParser.buildSort();
    this._view = {
      sortKey: sort.sortKey,
      reverse: sort.reverse,
      filters: ArnPlpUrlParser.buildFilters(),
      priceSorting: ArnPlpUrlParser.isPriceSorting(),
      priceDescending: ArnPlpUrlParser.isPriceSortDescending(),
      groupingDisabled: ArnPlpUrlParser.isGroupingDisabled(),
      hasFilters: ArnPlpUrlParser.hasActiveFilters(),
    };
    this._cursor = null;
    this._hasNextPage = true;
    this._renderedGroupKeys = new Set(); // plpCardGroupKey values, used in normal (grouped) mode
    this._renderedHandles = new Set(); // used in ungrouped mode (price sort)
    this._soldOutCards = [];
    this._validHandles = new Set();
    this._collectionMocoOrderByModel = new Map();
    this._collectionMocoOrderPromise = null;
    this._loading = false;
    this._totalRendered = 0;
    this._page = 1;

    // Price-sort buffer: whole collection fetched once and sorted by final
    // discounted price client-side (Storefront API can't sort on that).
    this._priceSortedBuffer = null;
    this._priceSortedIndex = 0;
    // Filtered views are materialized once so every matching color is known
    // before the first card is assembled.
    this._filteredCatalog = null;
    this._filteredCatalogIndex = 0;

    this._btn?.addEventListener('click', (e) => {
      e.preventDefault();
      if (this._loading) return;

      // Read target page directly from the href (?page=N). Copy any
      // filter/sort params the href dropped so a refresh stays on this view.
      const nextUrl = new URL(this._btn.href, window.location.origin);
      const current = new URL(window.location.href);
      for (const [key, value] of current.searchParams.entries()) {
        if (key === 'page') continue;
        if (!nextUrl.searchParams.has(key)) nextUrl.searchParams.append(key, value);
      }
      const nextPage = parseInt(nextUrl.searchParams.get('page'), 10);

      if (nextPage) {
        this._page = nextPage;
        window.history.replaceState({}, '', nextUrl);
      }

      this._skeletonMgr.preFill(ARN_CFG.TARGET_CARDS_PER_LOAD);
      this._fillToTarget();
    });

    ARN_LOG('connectedCallback — starting initial load');
    this._skeletonMgr.preFill(ARN_CFG.TARGET_CARDS_PER_LOAD);
    if (!this._view.groupingDisabled) {
      // Start the ST order scan immediately. _renderCards awaits the same
      // cached promise before it assembles the first grouped card.
      this._ensureCollectionMocoOrder();
    }
    this._fillToTarget();
  }

  // ── Fill loop ──────────────────────────────────────────────────────────────

  async _fillToTarget() {
    if (this._loading) return;
    this._setLoading(true);
    if (this._counter) this._counter.classList.add('tw-invisible');

    let newCards = 0;
    try {
      while (newCards < this._targetPerLoad && this._hasNextPage) {
        const count = await this._loadBatch();
        newCards += count;
        if (count === 0 && !this._hasNextPage) break;
      }
      ARN_LOG(`Load complete — ${newCards} new card(s) added to grid (total in grid: ${this._totalRendered})`);
    } catch (err) {
      ARN_ERR('Unhandled error during load:', err);
    } finally {
      this._setLoading(false);

      // Show empty state if the fill cycle completed with zero total rendered cards.
      if (this._totalRendered === 0) {
        this._showEmptyState();
      } else {
        this._updateCounter();
        this._syncButton();
        // Notify Tealium ONCE per fill cycle, not once per Storefront batch.
        // A single user action (initial load or Load More click) can require
        // multiple _loadBatch iterations to reach TARGET_CARDS_PER_LOAD, but
        // from Tealium's point of view it is one logical page view update.
        this._tealium.publishProductsLoaded();
        // Advance the logical page counter and update the Load More <a href>
        // so crawlers (and users right-clicking "open in new tab") see the
        // next ?page=N target. See _bumpPageHref for details.
        this._bumpPageHref(newCards);
        this._isFirstLoad = false;
      }
    }
  }

  /**
   * Replace the product grid with the empty state markup.
   *
   * Clones the #arn-plp-empty-state-template element pre-rendered by Liquid
   * in arn-main-collection-product-grid.liquid and replaces the current
   * #ProductGridContainer contents with it. The template contains
   * server-rendered translations and collection.url, so no i18n lookup or
   * URL construction is needed here.
   */
  _showEmptyState() {
    ARN_LOG('_showEmptyState — no products found, showing empty state');

    this._skeletonMgr.drainPending();

    const tmpl = document.getElementById('arn-plp-empty-state-template');
    if (!tmpl) {
      ARN_WARN('_showEmptyState — #arn-plp-empty-state-template not found in DOM');
      return;
    }

    const container = document.getElementById('ProductGridContainer');
    if (container) {
      container.innerHTML = tmpl.innerHTML;
      ARN_LOG('_showEmptyState — empty state injected into #ProductGridContainer');
    }

    if (this._counter) this._counter.classList.add('tw-hidden');
    if (this._btn) this._btn.classList.add('tw-hidden');
  }

  // ── Batch ──────────────────────────────────────────────────────────────────

  async _loadBatch() {
    const { sortKey, reverse, filters } = this._view;

    // Price sorting: serve from the final-price-sorted buffer (keyed on the
    // representative variant, like the card). Filtered views are materialized
    // up front so sibling cards only keep colors the filter actually returned.
    let products, hasNextPage;
    if (this._usePriceSortBuffer()) {
      ({ products, hasNextPage } = await this._nextPriceSortedPage(filters));
    } else if (filters.length) {
      ({ products, hasNextPage } = await this._nextFilteredPage(filters, sortKey, reverse));
    } else {
      let endCursor;
      ({ products, hasNextPage, endCursor } =
        await this._storefront.fetchCollectionPage(
          this._collectionHandle, this._cursor, filters, sortKey, reverse
        ));
      this._cursor = endCursor;
    }

    this._hasNextPage = hasNextPage;

    for (const p of products) this._validHandles.add(normalizeMocoHandle(p.handle));

    let groups;
    if (this._view.groupingDisabled) {
      ARN_LOG('Grouping disabled — each product shown as individual single-slide card');
      // In ungrouped mode (price sort) siblings of the same model appear at
      // different positions in the price-ordered result set and must each be
      // shown as their own card. Deduplicate only by handle to prevent the
      // same product from appearing twice across pagination boundaries.
      const uniqueProducts = products.filter(p => {
        const handle = normalizeMocoHandle(p.handle);
        if (this._renderedHandles.has(handle)) {
          ARN_LOG(`_loadBatch (ungrouped) — skip duplicate handle "${p.handle}"`);
          return false;
        }
        this._renderedHandles.add(handle);
        return true;
      });
      groups = uniqueProducts.map(p => ({ primary: p, siblingHandles: [], disableGrouping: true }));
      ARN_LOG(`Ungrouped: ${groups.length} card(s) from ${products.length} product(s) after handle deduplication`);
    } else {
      groups = this._deduplicateProducts(products);
      ARN_LOG(`After deduplication: ${groups.length} unique group(s) from ${products.length} product(s)`);
      if (groups.length) groups = await this._resolveSiblings(groups);
    }

    if (!groups.length && (this._hasNextPage || !this._soldOutCards.length)) {
      ARN_LOG('No new groups to render in this batch');
      return 0;
    }
    return this._renderCards(groups);
  }

  // ── Price-sort buffer ────────────────────────────────────────────────────────

  // Always sort price views on the representative variant price the card
  // shows. Storefront sortKey PRICE orders by the minimum variant price and
  // its cursor pages repeat products, so it cannot drive the grid.
  _usePriceSortBuffer() {
    return this._view.priceSorting;
  }

  // Sync DK applied codes (cookie/URL + cart) before computing final prices, so
  // code-based discounts are eligible at sort time. arn-dk-discount-early-sync
  // only covers cookie/URL/path codes — cart-applied codes need the async fetch.
  async _syncDkCodes() {
    const dk = window.ArnDkStorefrontPrices;
    if (!dk) return;
    try {
      dk.syncAppliedCodesFromCookies?.();
      await dk.syncFromCart?.();
    } catch (err) {
      ARN_WARN('_syncDkCodes — DK code sync failed:', err);
    }
  }

  // Serves the next page from the buffer (built on first access), same
  // { products, hasNextPage } contract as a Storefront page.
  async _nextPriceSortedPage(filters) {
    if (!this._priceSortedBuffer) {
      await this._buildPriceSortedBuffer(filters);
    }
    const start = this._priceSortedIndex;
    const slice = this._priceSortedBuffer.slice(start, start + ARN_CFG.PRODUCTS_PER_FETCH);
    this._priceSortedIndex = start + slice.length;
    return { products: slice, hasNextPage: this._priceSortedIndex < this._priceSortedBuffer.length };
  }

  /**
   * Fetch the whole filtered collection with a stable sort, then order it by
   * the price the card will display. Built once per price-sorted view.
   * If the priced query fails, the same walk is repeated without DK fields.
   *
   * @param {object[]} filters
   */
  async _buildPriceSortedBuffer(filters) {
    await this._syncDkCodes();
    const ctx = ArnPlpFinalPrice.dkCtx();
    const descending = this._view.priceDescending;
    let walked = await walkCollectionPages((cursor) => this._storefront.fetchCollectionPagePriced(
      this._collectionHandle, cursor, filters, 'COLLECTION_DEFAULT', false, ARN_CFG.PRODUCTS_PER_FETCH
    ));
    if (!walked.products.length) {
      ARN_WARN('_buildPriceSortedBuffer — priced walk returned nothing; retrying without DK fields');
      walked = await walkCollectionPages((cursor) => this._storefront.fetchCollectionPage(
        this._collectionHandle, cursor, filters, 'COLLECTION_DEFAULT', false
      ));
    }

    const scored = walked.products.map((node, order) => ({
      node,
      cents: ArnPlpFinalPrice.cents(node, ctx),
      order,
    }));
    scored.sort((a, b) => (descending ? b.cents - a.cents : a.cents - b.cents) || (a.order - b.order));
    this._priceSortedBuffer = scored.map((entry) => entry.node);
    this._priceSortedIndex = 0;

    ARN_LOG(`_buildPriceSortedBuffer — ${this._priceSortedBuffer.length} product(s) across ${walked.pages} page(s) sorted by final price (${descending ? 'desc' : 'asc'})`);
    if (ARN_DEBUG_LOGS) {
      ARN_LOG('_buildPriceSortedBuffer — order:', scored.slice(0, 60).map((entry) => `${entry.node.handle}=${(entry.cents / 100).toFixed(2)}`));
    }
  }

  /**
   * Serve the next render window from the materialized filtered catalog.
   *
   * @param {object[]} filters
   * @param {string} sortKey
   * @param {boolean} reverse
   * @returns {Promise<{products: object[], hasNextPage: boolean}>}
   */
  async _nextFilteredPage(filters, sortKey, reverse) {
    if (!this._filteredCatalog) {
      await this._buildFilteredCatalog(filters, sortKey, reverse);
    }
    const start = this._filteredCatalogIndex;
    const slice = this._filteredCatalog.slice(start, start + ARN_CFG.PRODUCTS_PER_FETCH);
    this._filteredCatalogIndex = start + slice.length;
    return {
      products: slice,
      hasNextPage: this._filteredCatalogIndex < this._filteredCatalog.length,
    };
  }

  /**
   * Read every product that matches the active filters before the first card
   * is built. Sibling lookup then knows the full match set, so a later page
   * cannot hide a matching color or let Liquid paint one that failed the filter.
   *
   * @param {object[]} filters
   * @param {string} sortKey
   * @param {boolean} reverse
   */
  async _buildFilteredCatalog(filters, sortKey, reverse) {
    const walked = await walkCollectionPages((cursor) => this._storefront.fetchCollectionPage(
      this._collectionHandle, cursor, filters, sortKey, reverse
    ));
    this._filteredCatalog = walked.products;
    this._filteredCatalogIndex = 0;
    this._siblingAvailability = this._siblingAvailability || {};
    this._siblingNodesByHandle = this._siblingNodesByHandle || {};
    for (const product of walked.products) {
      const handle = normalizeMocoHandle(product.handle);
      this._validHandles.add(handle);
      this._siblingAvailability[handle] = product.availableForSale;
      this._siblingNodesByHandle[handle] = product;
    }
    ARN_LOG(`_buildFilteredCatalog — ${walked.products.length} matching product(s) across ${walked.pages} page(s)`);
  }

  // ── ST collection moco order ───────────────────────────────────────────────

  /**
   * Share one collection-wide manual-order scan between every render batch.
   */
  _ensureCollectionMocoOrder() {
    if (!this._collectionMocoOrderPromise) {
      this._collectionMocoOrderPromise = this._loadCollectionMocoOrder();
    }
    return this._collectionMocoOrderPromise;
  }

  /**
   * Read the complete unfiltered manual collection order written by ST and
   * build an independent moco sequence for each model. Shopper filters/sorting
   * affect which cards and colors are visible, never their ST-owned sequence.
   */
  async _loadCollectionMocoOrder() {
    ARN_LOG('_loadCollectionMocoOrder — loading ST manual collection order');

    let cursor = null;
    let hasNextPage = true;
    let productCount = 0;

    try {
      while (hasNextPage) {
        const page = await this._storefront.fetchCollectionMocoOrderPage(
          this._collectionHandle,
          cursor
        );

        buildCollectionMocoOrderByModel(
          page.products,
          this._collectionMocoOrderByModel
        );
        productCount += page.products.length;
        hasNextPage = page.hasNextPage;
        cursor = page.endCursor;

        if (!page.products.length) break;
      }

      ARN_LOG(`_loadCollectionMocoOrder — ranked ${productCount} moco(s) across ${this._collectionMocoOrderByModel.size} model(s)`);
    } catch (err) {
      // Resolve the shared promise with an empty/partial map so the PLP can
      // retain its previous VariantHandles + availability fallback.
      ARN_ERR('_loadCollectionMocoOrder — manual collection scan failed:', err);
    }
  }

  // ── Deduplication ──────────────────────────────────────────────────────────

  _deduplicateProducts(products) {
    const newGroups = [];
    for (const p of products) {
      const key = plpCardGroupKey(p);
      if (this._renderedGroupKeys.has(key)) {
        ARN_LOG(`_deduplicateProducts — skip duplicate group key ${key} (handle: ${p.handle})`);
        continue;
      }
      this._renderedGroupKeys.add(key);
      newGroups.push(p);
    }

    // A null groupKey on a product with a model code usually means
    // custom.x_groupkey is not exposed to the Storefront API on this store.
    const withoutGroupKey = products.filter(p => p.modelCode?.value && !p.groupKey?.value).length;
    if (withoutGroupKey) {
      ARN_WARN(`_deduplicateProducts — ${withoutGroupKey} product(s) without custom.x_groupkey: grouped by model code only`);
    }
    return newGroups;
  }

  // ── Sibling resolution ─────────────────────────────────────────────────────

  async _resolveSiblings(products) {
    const toFetch = new Set();
    const hasFilters = this._view.hasFilters;

    for (const p of products) {
      const handles = this._parseSiblingHandles(p);
      ARN_LOG(`_resolveSiblings — "${p.handle}" parsed handles:`, handles);
      for (const h of handles) {
        // When filters are active, only include siblings already present in
        // _validHandles (i.e. confirmed by the filtered Storefront API query).
        // Siblings not in _validHandles did not pass the active filters and
        // must be excluded to avoid showing off-filter products in the card.
        if (hasFilters && !this._validHandles.has(h)) {
          ARN_LOG(`_resolveSiblings — "${h}" excluded: not in filtered results`);
          continue;
        }
        if (!this._validHandles.has(h)) toFetch.add(h);
      }
    }

    ARN_LOG(`_resolveSiblings — fetching ${toFetch.size} unknown handle(s) in a single batched query:`, [...toFetch]);

    // Lazily initialise _siblingNodesByHandle — used by Tealium ingestion
    // to look up resolved sibling product nodes. Also seeded with the primaries
    // from the current batch so the ingest step has a single source of truth
    // for "all known product nodes".
    this._siblingNodesByHandle = this._siblingNodesByHandle || {};

    // Seed with primaries from this batch (they may also appear as siblings
    // of another product, so we want them looked up consistently).
    for (const p of products) {
      this._siblingNodesByHandle[p.handle] = p;
    }

    if (toFetch.size) {
      const fetched = await this._storefront.fetchProductsByHandles([...toFetch]);

      let added = 0;
      for (const [h, p] of Object.entries(fetched)) {
        this._validHandles.add(h);
        this._siblingAvailability = this._siblingAvailability || {};
        this._siblingAvailability[h] = p.availableForSale;
        this._siblingNodesByHandle[h] = p;
        added++;
      }

      for (const h of toFetch) {
        if (!fetched[h]) ARN_WARN(`_resolveSiblings — "${h}" not found in store`);
      }

      ARN_LOG(`_resolveSiblings — added ${added} handles to validHandles`);
    }

    return products.map(p => {
      const allSiblings = this._parseSiblingHandles(p).filter(
        h => h !== p.handle && this._validHandles.has(h)
      );

      // Sort: available siblings first, unavailable last
      const avail = allSiblings.filter(h => this._siblingAvailability?.[h] !== false);
      const unavail = allSiblings.filter(h => this._siblingAvailability?.[h] === false);
      const siblings = [...avail, ...unavail];

      ARN_LOG(`_resolveSiblings — "${p.handle}" → valid siblings: [${siblings.join(', ')}] (${unavail.length} unavailable last)`);
      return { primary: p, siblingHandles: siblings, disableGrouping: false };
    });
  }

  /**
   * Feed the Tealium tracker with every product node visible in this batch:
   * the primaries (one per group) plus each sibling that was successfully
   * resolved into a real product node. Resolved sibling nodes live in
   * `_siblingNodesByHandle`, populated by `_resolveSiblings`.
   *
   * The tracker's internal handle-based de-duplication handles the case
   * where a primary and one of its own siblings are the same product (rare
   * but possible with messy metafield data), and the case where the same
   * sibling appears in multiple groups' lists.
   */
  _ingestGroupsIntoTealium(groups) {
    if (!groups?.length) return;

    const nodes = [];
    for (const g of groups) {
      if (g.primary) nodes.push(g.primary);
      for (const h of g.siblingHandles || []) {
        const sibNode = this._siblingNodesByHandle?.[h];
        if (sibNode) nodes.push(sibNode);
      }
    }
    this._tealium.ingestBatch(nodes);
  }

  _parseSiblingHandles(product) {
    const raw = product.variantsHandles?.value;
    if (!raw) return [];
    let handles;
    try {
      const parsed = JSON.parse(raw);
      handles = Array.isArray(parsed)
        ? parsed.map(h => String(h).trim())
        : [String(parsed).trim()];
    } catch {
      handles = raw.split(/[\n,]+/).map(h => h.trim());
    }
    return handles.filter(Boolean).map(h => this._normalizeHandle(h));
  }

  /**
   * Normalise a sibling handle to the canonical Shopify form (lowercase).
   * Shopify product handles are always lowercase; the metafield input is
   * lowercased defensively in case authors stored mixed-case values.
   */
  _normalizeHandle(raw) {
    return raw.toLowerCase();
  }

  // ── Rendering ──────────────────────────────────────────────────────────────

  _commitCard(skeleton, cardLi, group) {
    this._renderer.replaceSkeleton(skeleton, cardLi);
    if (!cardLi) return 0;
    this._totalRendered++;
    this._ingestGroupsIntoTealium([group]);
    this._mediaBlockMgr.insertDue(this._totalRendered);
    return 1;
  }

  /**
   * Render cards in chunks of RENDER_CONCURRENCY.
   *
   * Skeletons acquired here are always visible — see ArnPlpSkeletonManager
   * for the rationale. The render pipeline commits real cards to the DOM as
   * they become ready, swapping each skeleton in place. No visibility
   * coordination is needed: the user sees a uniform shimmer grid that
   * fills in card-by-card.
   *
   * At the end of the batch, the Tealium card count is updated so
   * searchResultItemsQnt reflects the now-visible grid size. The actual
   * plpProductsLoaded dispatch happens once per fill cycle in _fillToTarget,
   * not here (see setCardCount JSDoc).
   */
  async _renderCards(groups) {
    ARN_LOG(`_renderCards — rendering ${groups.length} card(s) in chunks of ${ARN_CFG.RENDER_CONCURRENCY}`);

    if (groups.some(group => !group.disableGrouping)) {
      await this._ensureCollectionMocoOrder();
    }

    // Acquire one skeleton per group (adopt pre-inserted ones first, then
    // create new ones if the queue runs out mid-batch).
    const skeletons = groups.map(() => this._skeletonMgr.takeOrCreate());

    // Any leftover pre-inserted skeletons (more were pre-filled than groups
    // produced) must be removed so they don't leave ghost cells in the grid.
    this._skeletonMgr.drainPending();

    let rendered = 0;

    for (let i = 0; i < groups.length; i += ARN_CFG.RENDER_CONCURRENCY) {
      const chunk = groups.slice(i, i + ARN_CFG.RENDER_CONCURRENCY);

      // Fetch + assemble all cards in the chunk in parallel
      const assembled = await Promise.all(
        chunk.map(({ primary, siblingHandles, disableGrouping }) => {
          const modelKey = collectionMocoModelKey(primary);
          const collectionMocoOrder =
            this._collectionMocoOrderByModel.get(modelKey) || [];

          const allowedHandles = (this._view.hasFilters || disableGrouping)
            ? [primary.handle, ...(disableGrouping ? [] : siblingHandles)]
            : null;

          return this._renderer.fetchAndAssembleCard(
            primary.handle,
            siblingHandles,
            disableGrouping,
            collectionMocoOrder,
            allowedHandles
          );
        })
      );

      // Commit cards: replaceSkeleton inserts into the live DOM and triggers
      // post-insertion Swiper init.
      for (let j = 0; j < chunk.length; j++) {
        const cardLi = assembled[j];
        const skeleton = skeletons[i + j];
        const slides = Array.from(cardLi?.querySelectorAll('.swiper-slide') || []);
        if (slides.length >= chunk[j].siblingHandles.length + 1 && slides.every(slide => slide.dataset.soldOut === 'true')) {
          // Liquid classifies every real size, including metafields not exposed to Storefront.
          // Defer the entire card only if all its colors are discontinued.
          // ponytail: buffer detached cards; cache handles instead if large catalogs exceed memory.
          this._soldOutCards.push({ cardLi, group: chunk[j] });
          this._renderer.replaceSkeleton(skeleton, null);
        } else {
          rendered += this._commitCard(skeleton, cardLi, chunk[j]);
        }
      }

      // Delay between chunks to give Shopify's section-render rate limiter
      // breathing room.
      if (ARN_CFG.RENDER_CHUNK_DELAY_MS > 0 && i + ARN_CFG.RENDER_CONCURRENCY < groups.length) {
        await sleep(ARN_CFG.RENDER_CHUNK_DELAY_MS);
      }

      // Notify Discount Kit after each chunk so it can apply sale pricing to
      // the cards just committed to the DOM.
      this._discountKit.notify();
    }

    // Keep Coming back soon in collection order across Load More pages.
    if (!this._hasNextPage) {
      for (const { cardLi, group } of this._soldOutCards.splice(0)) {
        rendered += this._commitCard(this._skeletonMgr.takeOrCreate(), cardLi, group);
      }
      this._discountKit.notify();
    }

    // Update the Tealium card count to reflect the now-visible grid size.
    // Matches Liquid's `searchResultItemsQnt: '{{ collection.products_count }}'`
    // but with the correct post-grouping count.
    this._tealium.setCardCount(this._totalRendered);

    ARN_LOG(`_renderCards — done. Rendered: ${rendered}/${groups.length}. Total in grid: ${this._totalRendered}`);
    return rendered;
  }

  // ── UI helpers ─────────────────────────────────────────────────────────────

  _bumpPageHref(newCards) {
    if (!this._btn) return;
    if (newCards <= 0) return;
    if (!this._hasNextPage) return;

    // Build the next URL by cloning the current location and setting/replacing
    // the page param. URLSearchParams handles the case where the current URL
    // already contains filters/sort params, preserving them in the output.
    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set('page', String(this._page + 1));

    this._btn.setAttribute('href', nextUrl.pathname + nextUrl.search);
    this._btn.setAttribute('rel', 'next');

    ARN_LOG(`Load More href updated to ?page=${this._page + 1}`);
  }

  _setLoading(state) {
    this._loading = state;
    if (this._btn) this._btn.disabled = state;
    this._btn?.classList.toggle('tw-hidden', state);
    this._spinner?.classList.toggle('tw-hidden', !state);
  }

  _syncButton() {
    if (!this._hasNextPage) {
      ARN_LOG('No more pages — Load More button removed');
      this._btn?.remove();
      this._btn = null;
    }
  }

  _updateCounter() {
    if (!this._counter || !this._grid) return;
    const count = this._grid.querySelectorAll('li .arn-product-card').length;
    const tmpl = this._counter.dataset.counterTemplate;
    if (tmpl) this._counter.textContent = tmpl.replace('__ARN_COUNT__', count);
    this._counter.classList.remove('tw-invisible');
  }
}

customElements.define('arn-load-more', ArnLoadMore);

// #endregion
